/* Avora Rewards — cart ladder, Gift Wallet picker and checkout handoff. */
(function () {
  'use strict';

  var root;
  var config;
  var core;
  var catalog = [];
  var cart = { items: [], total_price: 0, item_count: 0 };
  var state = {};
  var filterWithinWallet = false;
  var searchQuery = '';
  var activeCategory = 'all';
  var sortMode = 'recommended';
  var catalogLoading = false;
  var catalogLoaded = false;
  var catalogLoadError = false;
  var catalogPromise = null;
  var mutationQueue = Promise.resolve();
  var cartRequestEpoch = 0;
  var searchTimer = null;
  var sortCloseTimer = null;
  var catalogRenderLimit = 0;
  var pendingCatalogFocusIndex = -1;
  var activeDialog = null;
  var lastFocus = null;
  var lastFocusSelector = '';
  var checkoutPreparing = false;
  var checkoutGuardReady = false;
  var checkoutFallbackPreparing = false;
  var checkoutFailsafeInstalled = false;
  var paidEligibility = {};
  var paidEligibilityPromises = {};
  var giftVariantValidation = {};
  var imageIndexes = {};
  var variantSelections = {};
  var activeVariantDropdown = null;
  var touchStart = null;
  var reopenCartOnClose = false;
  var giftMutationPending = false;
  var previousBodyOverflow = '';
  var pickerReturnKey = 'avora_rewards_picker_return_v1';
  var pickerReturnMaxAge = 6 * 60 * 60 * 1000;
  var categoryDefinitions = [
    { id: 'all', label: window.AvoraI18n.text('All products') },
    { id: 'decor', label: 'Decor & styling', words: ['decor', 'vase', 'candle', 'mirror', 'frame', 'clock', 'ornament', 'sculpture', 'display', 'floral'] },
    { id: 'storage', label: 'Storage & organisation', words: ['storage', 'organizer', 'organiser', 'rack', 'hook', 'shelf', 'shelving', 'basket', 'bin', 'holder', 'stand', 'drawer', 'cabinet'] },
    { id: 'furniture', label: 'Furniture', words: ['table', 'chair', 'stool', 'bench', 'desk', 'console', 'cabinet', 'nightstand', 'sideboard', 'furniture'] },
    { id: 'kitchen', label: 'Kitchen & dining', words: ['kitchen', 'dining', 'coffee', 'tea', 'cup', 'mug', 'glass', 'bowl', 'plate', 'tray', 'cutlery', 'tongs', 'spoon', 'coaster', 'cook', 'bamboo food'] },
    { id: 'bathroom', label: 'Bathroom', words: ['bath', 'toilet', 'towel', 'soap', 'shower', 'vanity', 'sink'] },
    { id: 'office', label: 'Office & desk', words: ['office', 'desk', 'workspace', 'stationery', 'monitor', 'laptop', 'document'] },
    { id: 'textiles', label: 'Textiles', words: ['textile', 'rug', 'mat', 'curtain', 'cushion', 'pillow', 'blanket', 'throw', 'linen'] },
    { id: 'lighting', label: 'Lighting', words: ['light', 'lamp', 'lantern', 'sconce'] },
    { id: 'outdoor', label: 'Outdoor & garden', words: ['outdoor', 'garden', 'patio', 'balcony', 'plant', 'planter'] },
    { id: 'family', label: 'Pet & family', words: ['pet', 'cat', 'dog', 'kids', 'child', 'nursery', 'baby', 'toy'] }
  ];

  function parseJSON(id, fallback) {
    var node = document.getElementById(id);
    if (!node) return fallback;
    try { return JSON.parse(node.textContent); } catch (error) { return fallback; }
  }

  function readSession(key) {
    try { return sessionStorage.getItem(key); } catch (error) { return null; }
  }

  function clearRewardErrors() {
    document.querySelectorAll('[data-avora-reward-error]').forEach(function (notice) { notice.remove(); });
  }

  function showRewardError(message, reload) {
    var scope = activeDialog || document.querySelector('#avora-cart-drawer.is-open') || document.querySelector('main') || document.body;
    var notice = scope.querySelector('[data-avora-reward-error]');
    if (!notice) {
      notice = document.createElement('div');
      notice.className = 'avora-rewards__error';
      notice.setAttribute('data-avora-reward-error', '');
      notice.setAttribute('role', 'alert');
      notice.tabIndex = -1;
      scope.insertBefore(notice, scope.firstChild);
    }
    notice.textContent = message;
    if (reload) {
      var retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = window.AvoraI18n.text('Reload and try again');
      retry.addEventListener('click', function () { window.location.reload(); });
      notice.appendChild(retry);
    }
    notice.focus({ preventScroll: false });
  }

  function fetchJSON(url, options) {
    var settings = options || {};
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? window.setTimeout(function () { controller.abort(); }, settings.timeout || 12000) : null;
    return fetch(url, {
      method: settings.method || 'GET',
      credentials: 'same-origin',
      headers: Object.assign({ 'X-Requested-With': 'XMLHttpRequest' }, settings.headers || {}),
      body: settings.body,
      signal: controller ? controller.signal : undefined
    }).then(function (response) {
      if (!response.ok) throw new Error('Request failed with ' + response.status);
      return response.json();
    }).then(function (data) {
      return needsOrderDiscountPricing(data) ? hydrateOrderDiscountPricing(data) : data;
    }).finally(function () {
      if (timer) window.clearTimeout(timer);
    });
  }

  function needsOrderDiscountPricing(value) {
    return value && Array.isArray(value.items)
      && (value.cart_level_discount_applications || []).length > 0
      && value.items.some(function (item) { return item._avora_net_line_price == null; });
  }

  function hydrateOrderDiscountPricing(currentCart) {
    // Ajax final_line_price excludes order discounts. Liquid exposes their exact
    // per-line allocations, avoiding estimates around reward thresholds.
    return fetchJSON(normalisedRootUrl() + '?sections=avora-cart-pricing', { timeout: 10000 }).then(function (sections) {
      var html = sections && sections['avora-cart-pricing'];
      if (!html) throw new Error(window.AvoraI18n.text('Reward pricing unavailable'));
      var documentCopy = new DOMParser().parseFromString(html, 'text/html');
      var node = documentCopy.querySelector('[data-avora-cart-pricing]');
      var pricing = node && JSON.parse(node.textContent);
      if (!pricing || pricing.total_price !== currentCart.total_price
        || !Array.isArray(pricing.items) || pricing.items.length !== currentCart.items.length
        || pricing.items.reduce(function (sum, line) { return sum + line.net_line_price; }, 0) !== currentCart.total_price) {
        throw new Error('Cart changed during reward verification');
      }
      currentCart.items.forEach(function (item) {
        var line = pricing.items.find(function (candidate) { return candidate.key === item.key; });
        if (!line || line.quantity !== item.quantity || line.final_line_price !== item.final_line_price
          || !Number.isInteger(line.net_line_price) || line.net_line_price < 0 || line.net_line_price > item.final_line_price) {
          throw new Error('Cart changed during reward verification');
        }
        item._avora_net_line_price = line.net_line_price;
        // Receipt labels use the same verified per-line allocations as the total.
        delete item._avora_discount_allocations;
        if (Array.isArray(line.allocations) && line.allocations.every(function (allocation) {
          return Number.isInteger(allocation.amount) && allocation.amount >= 0;
        }) && line.allocations.reduce(function (sum, allocation) { return sum + allocation.amount; }, 0) === item.original_line_price - line.net_line_price) {
          item._avora_discount_allocations = line.allocations;
        }
      });
      return currentCart;
    });
  }

  function enqueueMutation(operation) {
    var run = mutationQueue.catch(function () {}).then(operation);
    mutationQueue = run.catch(function () {});
    return run;
  }

  function waitForActiveCartMutations() {
    var waits = [];
    if (window.avoraCartDrawer && typeof window.avoraCartDrawer.whenIdle === 'function') {
      waits.push(window.avoraCartDrawer.whenIdle());
    }
    if (window.AvoraCartUpsell && typeof window.AvoraCartUpsell.whenIdle === 'function') {
      waits.push(window.AvoraCartUpsell.whenIdle());
    }
    document.querySelectorAll('cart-items-component, cart-discount-component').forEach(function(component) {
      if (typeof component.whenIdle === 'function') waits.push(component.whenIdle());
    });
    return Promise.all(waits.map(function(wait) {
      return Promise.resolve(wait).catch(function() {});
    }));
  }

  function normaliseCatalogProduct(product) {
    if (!product || !product.handle || !Array.isArray(product.variants)) return null;
    product.variants = product.variants.filter(function (variant) { return variant && variant.id; }).map(function (variant) {
      variant.price = Number(variant.price) || 0;
      variant.options = Array.isArray(variant.options) ? variant.options : [];
      return variant;
    });
    var initialVariant = product.variants.filter(function (variant) {
      return String(variant.id) === String(product.variantId) && variant.available;
    })[0] || product.variants.filter(function (variant) { return variant.available; })[0] || product.variants[0];
    if (!initialVariant) return null;
    product.variantId = initialVariant.id;
    product.variantTitle = initialVariant.title;
    product.price = initialVariant.price;
    product.available = initialVariant.available;
    product.images = Array.isArray(product.images) && product.images.length
      ? product.images.filter(function (image) { return image && image.src; })
      : [{ src: product.image || (config && config.giftBoxImage), alt: product.title }];
    product.images = uniqueGiftImages(product.images);
    product.tags = Array.isArray(product.tags)
      ? product.tags
      : String(product.tags || '').split(',').map(function (tag) { return tag.trim(); }).filter(Boolean);
    product.productType = product.productType || product.product_type || '';
    if (productExcludedFromGiftWallet(product)) return null;
    product.category = inferProductCategory(product);
    product.searchText = buildProductSearchText(product);
    return product;
  }

  function giftImageKey(src) {
    try {
      var url = new URL(src, window.location.href);
      var asset = url.pathname.match(/^.*\/(files|products)\/(.+)$/);
      if (asset && (url.hostname === 'cdn.shopify.com' || /\/cdn\/shop\//.test(url.pathname))) return asset[1]+'/'+asset[2];
      return url.href;
    } catch (_) { return src; }
  }

  function uniqueGiftImages(images) {
    var seen = {};
    return images.filter(function(image) {
      if (!image || !image.src) return false;
      var key=giftImageKey(image.src);
      if (seen[key]) return false;
      seen[key]=true;
      return true;
    });
  }

  function remoteProductToCatalog(product) {
    if (!product || !product.handle) return null;
    var images = (product.images || []).slice(0, 6).map(function (image) {
      return {
        src: catalogueImageUrl(image.src),
        alt: image.alt || product.title
      };
    });
    var optionNames = (product.options || []).map(function (option) { return option.name; });
    var variants = (product.variants || []).map(function (variant) {
      var options = [variant.option1, variant.option2, variant.option3].filter(function (value) { return value !== null && typeof value !== 'undefined'; });
      var variantImage = variant.featured_image && variant.featured_image.src ? catalogueImageUrl(variant.featured_image.src) : (images[0] && images[0].src);
      return {
        id: variant.id,
        title: variant.title || 'Default Title',
        options: options,
        // Wallet gift values use Shopify original variant prices, as enforced by
        // hydrateGiftVariantValidation and core.rewardGiftLineValue. Paid sale
        // reductions must not also be applied to the gift quote.
        price: toCents(variant.price),
        available: !!variant.available,
        image: variantImage || (config && config.giftBoxImage),
        imageAlt: (variant.featured_image && variant.featured_image.alt) || product.title
      };
    });
    var firstAvailable = variants.filter(function (variant) { return variant.available; })[0] || variants[0];
    if (!firstAvailable) return null;
    var rootUrl = normalisedRootUrl();
    return normaliseCatalogProduct({
      id: product.id,
      title: product.title,
      handle: product.handle,
      giftCard: !!product.gift_card,
      url: rootUrl + 'products/' + product.handle,
      image: (images[0] && images[0].src) || (config && config.giftBoxImage),
      images: images,
      variantId: firstAvailable.id,
      variantTitle: firstAvailable.title,
      price: firstAvailable.price,
      available: firstAvailable.available,
      optionNames: optionNames,
      variants: variants,
      tags: product.tags || [],
      productType: product.product_type || ''
    });
  }

  function productExcludedFromGiftWallet(product) {
    var type = String(product && (product.productType || product.product_type) || '').toLowerCase();
    var handle = String(product && product.handle || '').toLowerCase();
    var tags = (product && Array.isArray(product.tags) ? product.tags : []).map(function (tag) {
      return String(tag || '').toLowerCase();
    });
    var explicitlyExcluded = tags.some(function (tag) {
      return tag === 'reward-excluded' || tag === 'gift-wallet-excluded' || tag === 'gift wallet excluded';
    });
    var giftCard = !!(product && (product.giftCard || product.gift_card))
      || type.indexOf('gift card') !== -1
      || type.indexOf('giftcard') !== -1
      || /(^|-)gift-card(s)?($|-)/.test(handle);
    var hasPricedVariant = !!(product && Array.isArray(product.variants) && product.variants.some(function (variant) {
      return variant && variant.available && Number(variant.price) >= 1;
    }));
    return giftCard || explicitlyExcluded || !hasPricedVariant;
  }

  function productExcludedFromRewardEarning(product) {
    var tags = (product && Array.isArray(product.tags) ? product.tags : []).map(function (tag) {
      return String(tag || '').toLowerCase();
    });
    return !!(product && (product.giftCard || product.gift_card)) || tags.some(function (tag) {
      return tag === 'reward-excluded' || tag === 'gift-wallet-excluded' || tag === 'gift wallet excluded';
    });
  }

  function toCents(value) {
    var numeric = Number.parseFloat(value);
    return Number.isFinite(numeric) ? Math.round(numeric * 100) : 0;
  }

  function checkoutSalePrice(cents) {
    var percentage = Number(config && config.dailySalePercentage) || 0;
    if (!config || !config.dailySaleEnabled || percentage <= 0) return cents;
    return cents - Math.floor(cents * percentage / 100);
  }

  function catalogueImageUrl(src, width) {
    if (!src) return config && config.giftBoxImage;
    // Catalog thumbnails can arrive with width=240 on the shop's own CDN URL.
    // Replace that constraint rather than upscaling the tiny bitmap on Retina.
    try {
      var url = new URL(src, window.location.href);
      if (url.hostname !== 'cdn.shopify.com' && !/^\/cdn\/shop\/(files|products)\//.test(url.pathname)) return src;
      url.searchParams.set('width', String(width || 720));
      url.searchParams.delete('height');
      return url.href;
    } catch (error) { return src; }
  }

  function catalogueImageSrcset(src) {
    if (!src || catalogueImageUrl(src, 360) === catalogueImageUrl(src, 720)) return '';
    return [360, 540, 720, 1080].map(function(width) {
      return catalogueImageUrl(src, width) + ' ' + width + 'w';
    }).join(', ');
  }

  function normalisedRootUrl() {
    var rootUrl = String((config && config.rootUrl) || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/');
    return rootUrl.replace(/\/?$/, '/');
  }

  function inferProductCategory(product) {
    var haystack = [product.title, product.productType, product.product_type]
      .concat(product.tags || [])
      .join(' ')
      .toLowerCase();
    for (var i = 1; i < categoryDefinitions.length; i += 1) {
      var definition = categoryDefinitions[i];
      if (definition.words.some(function (word) { return haystack.indexOf(word) !== -1; })) return definition.id;
    }
    return 'decor';
  }

  function categoryLabel(categoryId) {
    var match = categoryDefinitions.filter(function (category) { return category.id === categoryId; })[0];
    return match ? match.label : 'Decor & styling';
  }

  function buildProductSearchText(product) {
    return core.normaliseSearch([product.title, product.handle, product.productType, categoryLabel(product.category)]
      .concat(product.tags || [])
      .concat((product.variants || []).map(function (variant) { return variant.title; }))
      .join(' '));
  }

  function mergeCatalogProducts(products) {
    var byHandle = {};
    catalog.forEach(function (product, index) { byHandle[product.handle] = index; });
    products.forEach(function (product) {
      if (!product || !product.handle || !product.variants.some(function (variant) { return variant.available; })) return;
      if (typeof byHandle[product.handle] === 'number') catalog[byHandle[product.handle]] = product;
      else {
        byHandle[product.handle] = catalog.length;
        catalog.push(product);
      }
    });
  }

  function loadCompleteCatalog() {
    if (catalogLoaded || catalogPromise) return catalogPromise || Promise.resolve(catalog);
    catalogLoading = true;
    catalogLoadError = false;

    function loadPage(page) {
      var endpoint = normalisedRootUrl() + 'products.json?limit=250&page=' + page;
      return fetchCatalogPage(endpoint, 0)
        .then(function (payload) {
          // A failed/invalid catalogue response is not an empty search result.
          if (!payload || !Array.isArray(payload.products)) throw new Error('Invalid product catalogue response');
          var rawProducts = payload.products;
          mergeCatalogProducts(rawProducts.map(remoteProductToCatalog).filter(Boolean));
          return rawProducts.length;
        });
    }

    function loadBatch(firstPage) {
      var pages = [firstPage, firstPage + 1, firstPage + 2].filter(function (page) { return page <= 20; });
      return Promise.all(pages.map(loadPage)).then(function (counts) {
        if (activeDialog && activeDialog.matches('[data-avora-gift-picker]')) renderGiftGrid();
        var fullBatch = counts.length === pages.length && counts.every(function (count) { return count === 250; });
        return fullBatch && pages[pages.length - 1] < 20 ? loadBatch(pages[pages.length - 1] + 1) : catalog;
      });
    }

    catalogPromise = loadPage(1).then(function (firstCount) {
      return firstCount === 250 ? loadBatch(2) : catalog;
    }).then(function () {
      catalogLoaded = true;
      catalogLoading = false;
      catalogPromise = null;
      if (activeDialog && activeDialog.matches('[data-avora-gift-picker]')) renderGiftGrid();
      return catalog;
    }).catch(function () {
      catalogLoading = false;
      catalogLoadError = true;
      catalogPromise = null;
      if (activeDialog && activeDialog.matches('[data-avora-gift-picker]')) renderGiftGrid();
      return catalog;
    });
    return catalogPromise;
  }

  function fetchCatalogPage(endpoint, attempt) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? window.setTimeout(function () { controller.abort(); }, 12000) : null;
    return fetch(endpoint, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' }, signal: controller ? controller.signal : undefined }).then(function (response) {
      if (response.status === 429 && attempt < 3) {
        return new Promise(function (resolve) { window.setTimeout(resolve, 650 * (attempt + 1)); }).then(function () {
          return fetchCatalogPage(endpoint, attempt + 1);
        });
      }
      if (!response.ok) throw new Error('Product catalogue request failed with ' + response.status);
      return response.json();
    }).finally(function () {
      if (timer) window.clearTimeout(timer);
    });
  }

  function init() {
    root = document.querySelector('[data-avora-rewards]');
    var runtimeConfig = parseJSON('avora-rewards-config', null);
    core = window.AvoraRewardsCore;
    var localizer = window.AvoraMarketLocalization;
    installCheckoutFailsafe(runtimeConfig);
    if (!root || !runtimeConfig || !runtimeConfig.enabled || !runtimeConfig.configUrl || !core || !localizer) {
      resumeFallbackCheckout(runtimeConfig);
      return;
    }
    fetchJSON(runtimeConfig.configUrl, { timeout: 8000 }).then(function (sharedConfig) {
      if (!sharedConfig || !sharedConfig.version || !Array.isArray(sharedConfig.tiers)) throw new Error('Invalid rewards configuration');
      var marketContext = localizer.context(window);
      var localizedConfig = localizer.localizeConfig(sharedConfig, marketContext);
      if (!localizedConfig || runtimeConfig.currency !== marketContext.currency) throw new Error(window.AvoraI18n.text('Rewards market conversion is unavailable'));
      config = Object.assign({}, localizedConfig, runtimeConfig, {
        currency: marketContext.currency,
        currencyRate: marketContext.rate,
        locale: marketContext.locale,
        tiers: localizedConfig.tiers
      });
      window.__avoraRewardsLocalizedConfig = config;
      document.dispatchEvent(new CustomEvent('avora:rewards-config-ready', { detail: config }));
      start();
      checkoutGuardReady = true;
      resumeCheckoutFromHash();
    }).catch(function () {
      root.hidden = true;
      resumeFallbackCheckout(runtimeConfig);
    });
  }

  function checkoutElements() {
    return document.querySelectorAll('[data-avora-checkout], button[name="checkout"], input[name="checkout"]');
  }

  function setFallbackCheckoutBusy(busy) {
    checkoutFallbackPreparing = !!busy;
    checkoutElements().forEach(function (checkout) {
      checkout.setAttribute('aria-busy', String(!!busy));
      if (!checkout.hasAttribute('data-avora-fallback-idle-disabled')) {
        checkout.setAttribute('data-avora-fallback-idle-disabled', checkout.disabled ? 'true' : 'false');
      }
      if (checkout.matches('button, input')) {
        checkout.disabled = !!busy || checkout.getAttribute('data-avora-fallback-idle-disabled') === 'true';
      }
      var label = checkout.querySelector('[data-avora-checkout-label], .button-text');
      if (label && !label.hasAttribute('data-avora-fallback-idle-label')) {
        label.setAttribute('data-avora-fallback-idle-label', label.textContent.trim());
      }
      if (label) label.textContent = busy ? window.AvoraI18n.text('Securing your cart…') : (label.getAttribute('data-avora-fallback-idle-label') || window.AvoraI18n.text('Checkout securely'));
    });
  }

  function fallbackDiscountCodes(currentCart, runtimeConfig) {
    var managed = Object.keys(runtimeConfig && runtimeConfig.codes || {}).map(function (key) {
      return String(runtimeConfig.codes[key] || '').toLowerCase();
    });
    var seen = {};
    var codes = [];
    function add(code) {
      var value = String(code || '').trim();
      var lower = value.toLowerCase();
      if (!value || seen[lower] || managed.indexOf(lower) !== -1 || lower.indexOf('avoragift') === 0) return;
      seen[lower] = true;
      codes.push(value);
    }
    (currentCart.discount_codes || []).forEach(function (discount) { add(discount && discount.code); });
    (currentCart.cart_level_discount_applications || []).forEach(function (discount) { add(discount && discount.title); });
    (currentCart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (allocation) {
        add(allocation && allocation.discount_application && allocation.discount_application.title);
      });
    });
    return codes;
  }

  function secureFallbackCheckout(runtimeConfig) {
    if (checkoutFallbackPreparing || checkoutGuardReady) return;
    setFallbackCheckoutBusy(false);
    showRewardError(window.AvoraI18n.text('We could not load your rewards. Your cart and gift selections have been kept. Reload this page before checking out.'), true);
  }

  function resumeFallbackCheckout(runtimeConfig) {
    if (window.location.hash === '#avora-checkout') secureFallbackCheckout(runtimeConfig || {});
  }

  function resumeCheckoutFromHash() {
    if (window.location.hash !== '#avora-checkout') return;
    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    beginCheckout(false);
  }

  function installCheckoutFailsafe(runtimeConfig) {
    if (checkoutFailsafeInstalled) return;
    checkoutFailsafeInstalled = true;
    document.addEventListener('click', function (event) {
      if (checkoutGuardReady) return;
      var checkout = event.target.closest('[data-avora-checkout], button[name="checkout"], input[name="checkout"]');
      if (!checkout || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      secureFallbackCheckout(runtimeConfig || {});
    }, true);
    window.addEventListener('hashchange', function () {
      if (window.location.hash !== '#avora-checkout') return;
      if (checkoutGuardReady) resumeCheckoutFromHash();
      else secureFallbackCheckout(runtimeConfig || {});
    });
  }

  function syncRoot() {
    var liveRoot = document.querySelector('[data-avora-rewards]');
    if (liveRoot && liveRoot !== root) root = liveRoot;
    return root;
  }

  function start() {
    var savedReturn = readPickerReturn();
    if (savedReturn && savedReturn.imageIndexes) imageIndexes = savedReturn.imageIndexes;
    if (savedReturn && savedReturn.variantSelections) variantSelections = savedReturn.variantSelections;
    if (savedReturn && typeof savedReturn.filterWithinWallet === 'boolean') filterWithinWallet = savedReturn.filterWithinWallet;
    if (savedReturn && typeof savedReturn.searchQuery === 'string') searchQuery = savedReturn.searchQuery;
    if (savedReturn && typeof savedReturn.activeCategory === 'string') activeCategory = savedReturn.activeCategory;
    if (savedReturn && typeof savedReturn.sortMode === 'string') sortMode = savedReturn.sortMode;
    if (!categoryDefinitions.some(function (category) { return category.id === activeCategory; })) activeCategory = 'all';
    if (['recommended', 'price-asc', 'price-desc', 'title'].indexOf(sortMode) === -1) sortMode = 'recommended';

    bindControls();
    fetchCart().then(restorePickerJourney);

    document.addEventListener('cart:update', handleCartEvent);
    // Promo changes alter paid eligibility, wallet tiers and every receipt.
    document.addEventListener('discount:update', fetchCart);
    document.addEventListener('cart:refresh', fetchCart);
    document.addEventListener('avora:cart-rendered', function (event) {
      if (event.detail && event.detail.cart) update(event.detail.cart);
    });
    window.addEventListener('pageshow', function (event) {
      if (checkoutPreparing) setCheckoutPreparing(false);
      setGuardContinueBusy(false);
      if (event.persisted) fetchCart().then(restorePickerJourney);
    });
  }

  function bindControls() {
    document.addEventListener('click',function(event) {
      var card=event.target.closest('[data-avora-gift-card]');
      if (card && event.target.closest('[data-avora-product-link]') && Date.now()<(card._giftSuppressClickUntil || 0)) {
        event.preventDefault();event.stopImmediatePropagation();
      }
    },true);
    ['pointerover','focusin'].forEach(function(type) {
      document.addEventListener(type,function(event) {
        var card=event.target.closest('[data-avora-gift-card]');
        if (card) warmGiftGallery(card);
      },{passive:true});
    });
    document.addEventListener('click', function (event) {
      if (!syncRoot()) return;
      var productLink = event.target.closest('[data-avora-product-link]');
      if (productLink) {
        rememberPickerJourney(productLink);
        return;
      }

      if (!event.target.closest('[data-avora-sort]')) closeSortMenu(false);
      var action = event.target.closest('button, [data-avora-checkout], [data-avora-rewards-scrim]');
      if (!action) return;

      if (action.matches('[data-avora-variant-trigger]')) toggleVariantDropdown(action);
      else if (action.matches('[data-avora-variant-option]')) chooseGiftVariant(action);
      else if (action.matches('[data-avora-variant-close], [data-avora-variant-backdrop]')) closeVariantDropdown(true);
      else if (action.matches('[data-avora-open-rewards]')) openDialog('map');
      else if (action.matches('[data-avora-collapse-dock]')) collapseDock();
      else if (action.matches('[data-avora-reward-dock-min]')) restoreDock();
      else if (action.matches('[data-avora-close-sheet], [data-avora-rewards-scrim]')) closeDialogs();
      else if (action.matches('[data-avora-celebration-close]')) hideTierCelebration(action.closest('[data-avora-celebration]'));
      else if (action.matches('[data-avora-celebration-choose]')) openCelebrationGiftPicker(action);
      else if (action.matches('[data-avora-open-gifts]')) openDialog('picker');
      else if (action.matches('[data-avora-guard-choose]')) guardPrimaryAction();
      else if (action.matches('[data-avora-picker-done]')) finishPicking();
      else if (action.matches('[data-avora-pdp-gift-action]')) addProductPageGift(action);
      else if (action.matches('[data-avora-picker-filter]')) toggleFilter(action);
      else if (action.matches('[data-avora-category]')) chooseCategory(action);
      else if (action.matches('[data-avora-search-clear]')) clearGiftSearch();
      else if (action.matches('[data-avora-filter-reset]')) resetCatalogFilters();
      else if (action.matches('[data-avora-catalog-retry]')) { loadCompleteCatalog(); renderGiftGrid(); }
      else if (action.matches('[data-avora-sort-trigger]')) toggleSortMenu(action);
      else if (action.matches('[data-avora-sort-option]')) chooseSortMode(action);
      else if (action.matches('[data-avora-catalog-more]')) showMoreCatalog(action);
      else if (action.matches('[data-avora-gift-quantity]')) updateGiftQuantity(action);
      else if (action.matches('[data-avora-gift-action]')) toggleGift(action);
      else if (action.matches('[data-avora-image-prev], [data-avora-image-next]')) changeGiftImage(action);
      else if (action.matches('[data-avora-dismiss-return]')) dismissPickerReturn();
      else if (action.matches('[data-avora-map-primary]')) mapPrimaryAction();
      else if (action.matches('[data-avora-guard-continue]')) continueCheckout();
    });

    document.addEventListener('change', function (event) {
      var select = event.target.closest('[data-avora-variant-select]');
      if (select) selectGiftVariant(select);
    });

    var search = root.querySelector('[data-avora-gift-search]');
    if (search) {
      search.value = searchQuery;
      var updateSearch = function (event) {
        window.clearTimeout(searchTimer);
        if (event && event.isComposing) return;
        searchQuery = core.normaliseSearch(search.value);
        resetCatalogRenderLimit();
        var clear = root.querySelector('[data-avora-search-clear]');
        if (clear) clear.hidden = !searchQuery;
        searchTimer = window.setTimeout(function () {
          updateStoredPickerState();
          renderGiftGrid();
        }, 90);
      };
      search.addEventListener('input', updateSearch);
      search.addEventListener('compositionstart', function () { window.clearTimeout(searchTimer); });
      search.addEventListener('compositionend', updateSearch);
    }

    document.addEventListener('avora:product-variant', handleProductVariant);

    document.addEventListener('touchstart', function (event) {
      var media = event.target.closest('[data-avora-gift-media]');
      if (!media || event.target.closest('button') || !event.touches || event.touches.length !== 1) return;
      touchStart = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
        handle: media.closest('[data-avora-gift-card]').dataset.productHandle
      };
    }, { passive: true });

    document.addEventListener('touchend', function (event) {
      if (!touchStart || !event.changedTouches || event.changedTouches.length !== 1) return;
      var media = event.target.closest('[data-avora-gift-media]');
      var card = media && media.closest('[data-avora-gift-card]');
      var end = event.changedTouches[0];
      var horizontal = end.clientX - touchStart.x;
      var vertical = end.clientY - touchStart.y;
      if (card && card.dataset.productHandle === touchStart.handle && Math.abs(horizontal) > 44 && Math.abs(horizontal) > Math.abs(vertical) * 1.2) {
        card._giftSuppressClickUntil=Date.now()+500;
        setGiftImage(card, horizontal < 0 ? 1 : -1, true);
      }
      touchStart = null;
    }, { passive: true });

    document.addEventListener('touchcancel',function(){touchStart=null;},{passive:true});

    document.addEventListener('click', function (event) {
      var checkout = event.target.closest('[data-avora-checkout], button[name="checkout"], input[name="checkout"]');
      if (!checkout || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (checkoutPreparing) return;
      if (core.requiresGiftDecision(state)) {
        openGuard(Math.max(0, state.wallet - state.giftTotal));
        return;
      }
      beginCheckout(false);
    }, true);

    document.addEventListener('keydown', function (event) {
      if (event.target.closest && event.target.closest('.avora-cart-confirm')) return;
      if (handleSortKeydown(event)) return;
      if (handleVariantDropdownKeydown(event)) return;
      if (!activeDialog) return;
      if (event.key === 'Escape') { closeDialogs(); return; }
      var media = event.target.closest('[data-avora-gift-media]');
      if (media && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
        event.preventDefault();
        setGiftImage(media.closest('[data-avora-gift-card]'), event.key === 'ArrowRight' ? 1 : -1, true);
        return;
      }
      if (event.key !== 'Tab') return;
      var focusable = Array.prototype.filter.call(activeDialog.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'), function (element) {
        return !element.closest('[hidden], [inert], [aria-hidden="true"]') && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
      });
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var heading = activeDialog.querySelector('h2[tabindex="-1"]');
      if (event.shiftKey && (document.activeElement === first || document.activeElement === heading)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });

    window.addEventListener('resize', positionVariantDropdown);
    document.addEventListener('scroll', positionVariantDropdown, true);
  }

  function handleCartEvent(event) {
    if (event.detail && event.detail.cart) update(event.detail.cart);
    else fetchCart();
  }

  function fetchCart() {
    var epoch = ++cartRequestEpoch;
    return fetchJSON(normalisedRootUrl() + 'cart.js', { timeout: 10000 })
      .then(function (nextCart) {
        if (epoch !== cartRequestEpoch) return null;
        update(nextCart);
        var hydratedEpoch = cartRequestEpoch;
        hydratePaidEligibility(nextCart, false).then(function () {
          if (hydratedEpoch === cartRequestEpoch) update(nextCart);
        });
        return nextCart;
      })
      .catch(function () { return null; });
  }

  function update(nextCart) {
    if (!nextCart || !Array.isArray(nextCart.items)) return;
    // The drawer can render before the shared config fetch resolves. Rendering
    // then would throw on config.tiers and abort the drawer's own render; start()
    // fetches the cart again once config is ready, so skipping here loses nothing.
    if (!config) return;
    if (needsOrderDiscountPricing(nextCart)) { fetchCart(); return; }
    if (!syncRoot()) return;
    cartRequestEpoch += 1;
    var previousState = state;
    cart = nextCart;
    state = calculateState(cart);
    renderPickerReturn();
    renderDock(previousState);
    renderDrawerJourney(previousState);
    renderWalletSummary(previousState);
    renderCartPageRewards(previousState);
    renderLadder();
    renderProductGift();
    if (activeDialog && activeDialog.matches('[data-avora-gift-picker]')) renderGiftGrid();
    else updatePickerTotals();
    syncGiftQuantityControls();
    updateCheckout();
    celebrateNewTier(previousState);
    if (typeof document !== 'undefined' && typeof CustomEvent === 'function') {
      document.dispatchEvent(new CustomEvent('avora:reward-state', { detail: { cart: cart, state: state, config: config } }));
    }
  }

  function isRewardGift(item) {
    return core.isRewardGift(item);
  }

  function calculateState(currentCart) {
    return core.calculateState(currentCart, config.tiers, config.codes, paidEligibility);
  }

  function hydratePaidEligibility(currentCart, strict) {
    var requests = [];
    (currentCart && currentCart.items || []).forEach(function (item) {
      if (!item) return;
      var productId = item.product_id != null ? String(item.product_id) : '';
      var handle = String(item.handle || '').trim();
      if (!productId || !handle || typeof paidEligibility[productId] === 'boolean') {
        if (strict && (!productId || !handle)) requests.push(Promise.reject(new Error('Missing product eligibility data')));
        return;
      }
      if (!paidEligibilityPromises[productId]) {
        paidEligibilityPromises[productId] = fetchJSON(normalisedRootUrl() + 'products/' + encodeURIComponent(handle) + '.js', { timeout: 10000 })
          .then(function (product) {
            paidEligibility[productId] = productExcludedFromRewardEarning(product);
            return paidEligibility[productId];
          }).catch(function (error) {
            delete paidEligibilityPromises[productId];
            throw error;
          });
      }
      requests.push(paidEligibilityPromises[productId]);
    });
    if (!strict) return Promise.all(requests.map(function (request) { return request.catch(function () { return null; }); }));
    return Promise.all(requests);
  }

  function hydrateGiftVariantValidation(currentCart, strict) {
    var groups = {};
    (currentCart && currentCart.items || []).forEach(function(item) {
      if (!isRewardGift(item)) return;
      var handle = String(item.handle || '').trim();
      var variantId = String(item.variant_id || '');
      giftVariantValidation[variantId] = false;
      if (!handle || !variantId) return;
      if (!groups[handle]) groups[handle] = [];
      groups[handle].push(item);
    });

    var requests = Object.keys(groups).map(function(handle) {
      return fetchJSON(normalisedRootUrl() + 'products/' + encodeURIComponent(handle) + '.js', { timeout: 10000 })
        .then(function(product) {
          groups[handle].forEach(function(item) {
            var variant = (product.variants || []).find(function(candidate) {
              return String(candidate.id) === String(item.variant_id);
            });
            var cartUnitPrice = Number(item.original_price) || 0;
            giftVariantValidation[String(item.variant_id)] = !!variant
              && variant.available === true
              && Number(variant.price) > 0
              && Number(variant.price) === cartUnitPrice;
          });
        });
    });
    if (!strict) return Promise.all(requests.map(function(request) { return request.catch(function() {}); }));
    return Promise.all(requests);
  }

  function confirmedWalletRemaining() {
    if (typeof state.walletRemaining === 'number') return Math.max(0, state.walletRemaining);
    return Math.max(0, (Number(state.wallet) || 0) - (Number(state.appliedCredit) || 0));
  }

  function renderDock(previousState) {
    var shell = root.querySelector('[data-avora-reward-dock-shell]');
    var dock = root.querySelector('[data-avora-reward-dock]');
    var mini = root.querySelector('[data-avora-reward-dock-min]');
    if (!shell || !dock || !mini) return;
    if (!state.paidCount || shouldShowPickerReturn()) {
      shell.hidden = true;
      mini.hidden = true;
      return;
    }

    var collapsed = readSession('avora_rewards_dock_collapsed') === 'true';
    shell.hidden = collapsed;
    mini.hidden = !collapsed;
    var title = dock.querySelector('[data-avora-dock-title]');
    var detail = dock.querySelector('[data-avora-dock-detail]');
    var earned = shell.querySelector('[data-avora-dock-earned]');
    var earnedCopy = shell.querySelector('[data-avora-dock-earned-copy]');
    var progressTrack = shell.querySelector('[data-avora-dock-progress-track]');
    var fill = shell.querySelector('[data-avora-dock-progress]');
    var progress = segmentProgress();

    if (fill) fill.style.transform = 'scaleX(' + (progress / 100) + ')';
    if (progressTrack) {
      progressTrack.setAttribute('aria-valuenow', String(Math.round(progress)));
    }
    if (state.nextTier) {
      var gap = state.nextTier.threshold - state.paidSubtotal;
      title.textContent = window.AvoraI18n.text('{amount} away', {amount: money(gap)});
      detail.textContent = window.AvoraI18n.text(state.currentTier ? 'Next: {reward} at {amount}' : 'Unlock {reward} at {amount}', {reward: state.nextTier.title, amount: money(state.nextTier.threshold)});
      if (earned && earnedCopy) {
        earned.hidden = !state.wallet;
        earnedCopy.textContent = state.wallet ? money(state.wallet) + window.AvoraI18n.text(' Gift Wallet unlocked') : '';
      }
      if (progressTrack) progressTrack.setAttribute('aria-valuetext', money(gap) + window.AvoraI18n.text(' away from ') + state.nextTier.title);
    } else {
      title.textContent = window.AvoraI18n.text('All rewards unlocked');
      detail.textContent = money(state.wallet) + window.AvoraI18n.text(' Gift Wallet + ') + money(state.futureCard) + window.AvoraI18n.text(' Reward Card secured');
      if (earned && earnedCopy) {
        earned.hidden = false;
        earnedCopy.textContent = window.AvoraI18n.text('Every reward secured');
      }
      if (progressTrack) progressTrack.setAttribute('aria-valuetext', window.AvoraI18n.text('All Avora rewards unlocked'));
      if (fill) fill.style.transform = 'scaleX(1)';
    }

    if (amountIncreased(previousState, 'paidSubtotal')) replayMotion(dock, 'is-progressing', 900);
    if (amountIncreased(previousState, 'wallet')) replayMotion(dock, 'is-unlocked', 1200);
  }

  function collapseDock() {
    try { sessionStorage.setItem('avora_rewards_dock_collapsed', 'true'); } catch (error) {}
    renderDock();
    if (window.AvoraCartInteractions) window.AvoraCartInteractions.refreshPresentation();
    window.requestAnimationFrame(function () {
      var mini = root.querySelector('[data-avora-reward-dock-min]');
      if (mini && !mini.hidden) mini.focus({ preventScroll: true });
    });
  }

  function restoreDock() {
    try { sessionStorage.removeItem('avora_rewards_dock_collapsed'); } catch (error) {}
    renderDock();
    if (window.AvoraCartInteractions) window.AvoraCartInteractions.refreshPresentation();
    window.requestAnimationFrame(function () {
      var view = root.querySelector('[data-avora-reward-dock-shell] [data-avora-open-cart]') || root.querySelector('[data-avora-reward-dock-shell] [data-avora-open-rewards]');
      if (view && !view.hidden) view.focus({ preventScroll: true });
    });
  }

  function segmentProgress() {
    if (!state.nextTier) return 100;
    var previous = state.currentTier ? state.currentTier.threshold : 0;
    return Math.max(0, Math.min(100, ((state.paidSubtotal - previous) / (state.nextTier.threshold - previous)) * 100));
  }

  function renderDrawerJourney(previousState) {
    var journey = document.getElementById('avora-shipping-bar');
    if (!journey) return;
    journey.style.display = state.paidCount ? '' : 'none';
    if (!state.paidCount) return;

    var copy = journey.querySelector('[data-avora-rewards-next]');
    var fill = journey.querySelector('[data-avora-rewards-progress]');
    var track = fill && fill.parentElement;
    var markers = journey.querySelector('[data-avora-reward-markers]');
    var progress = core.ladderProgress(state.paidSubtotal, config.tiers);
    if (copy) {
      // One sentence, one type size: the amount and benefit must read together.
      copy.innerHTML = state.nextTier
        ? '<span class="avora-drawer__journey-intro">' + escapeHTML(window.AvoraI18n.text('Spend another ')) + '<strong>' + escapeHTML(money(state.nextTier.threshold - state.paidSubtotal)) + '</strong>' + escapeHTML(window.AvoraI18n.text(' to get')) + '</span> ' +
          '<span class="avora-drawer__journey-reward">' + escapeHTML(money(state.nextTier.wallet)) + ' <span>' + escapeHTML(window.AvoraI18n.text('Gift Wallet')) + '</span></span>'
        : '<span class="avora-drawer__journey-intro">' + escapeHTML(window.AvoraI18n.text('You’ve unlocked all rewards')) + '</span> · ' +
          '<span class="avora-drawer__journey-reward">' + escapeHTML(money(state.wallet)) + ' <span>' + escapeHTML(window.AvoraI18n.text('Gift Wallet')) + '</span></span>';
      copy.innerHTML = '<button type="button" class="avora-drawer__journey-icon" data-avora-open-rewards aria-label="'+escapeAttr(window.AvoraI18n.text('See all rewards'))+'"><svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 11h16v10H4zM3 7h18v4H3zM12 7v14M12 7H8a3 3 0 1 1 3-3l1 3Zm0 0h4a3 3 0 1 0-3-3l-1 3Z"/></svg></button><span class="avora-drawer__journey-promise">' + copy.innerHTML + '</span>';
    }
    if (fill) {
      fill.style.transform = 'scaleX(' + (progress / 100) + ')';
      fill.classList.toggle('is-complete', !state.nextTier);
      if (amountChanged(previousState, 'paidSubtotal')) replayMotion(fill, 'is-animating', 900);
    }
    if (track) {
      track.setAttribute('aria-valuenow', String(Math.round(progress)));
      var describedTier = state.currentTier || state.nextTier;
      var stageDescription = describedTier ? window.AvoraI18n.text(state.currentTier ? 'Current' : 'Next') + ': ' + window.AvoraI18n.text('{wallet} Gift Wallet', {wallet:moneyShort(describedTier.wallet)}) + '. ' : '';
      track.setAttribute('aria-valuetext', stageDescription + (copy ? copy.textContent : Math.round(progress) + '% complete'));
    }
    if (markers) {
      markers.innerHTML = config.tiers.map(function (tier, index) {
        var unlocked = state.paidSubtotal >= tier.threshold;
        var newlyUnlocked = unlocked && previousState && typeof previousState.paidSubtotal === 'number' && previousState.paidSubtotal < tier.threshold;
        var previousThreshold = index ? config.tiers[index - 1].threshold : 0;
        var completion = Math.max(0, Math.min(1, (state.paidSubtotal - previousThreshold) / (tier.threshold - previousThreshold)));
        var isCurrent = state.currentTier && state.currentTier.threshold === tier.threshold;
        var isNext = state.nextTier && state.nextTier.threshold === tier.threshold;
        var isFirstTarget = !state.currentTier && isNext;
        return '<span class="avora-drawer__milestone' + (unlocked ? ' is-unlocked' : '') + (isCurrent ? ' is-current' : '') + (isNext ? ' is-next' : '') + (isFirstTarget ? ' is-first-target' : '') + (newlyUnlocked ? ' is-new' : '') + '" style="--milestone-fill:' + completion + '">' +
          '<span class="avora-drawer__milestone-label">' + (unlocked ? config.checkmarkIcon : '') + moneyShort(tier.threshold) + '</span>' +
          '<span class="avora-drawer__milestone-track"><span></span></span>' +
          '<span class="avora-drawer__milestone-reward" aria-label="' + escapeAttr(window.AvoraI18n.text('{wallet} Gift Wallet', {wallet:moneyShort(tier.wallet)})) + '" title="' + escapeAttr(tier.title || '') + '">' +
            '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 11h16v10H4zM3 7h18v4H3zM12 7v14M12 7H8a3 3 0 1 1 3-3l1 3Zm0 0h4a3 3 0 1 0-3-3l-1 3Z"/></svg><strong>' + escapeHTML(moneyShort(tier.wallet)) + '</strong></span>' +
        '</span>';
      }).join('');
    }
    if (amountIncreased(previousState, 'paidSubtotal')) replayMotion(journey, 'is-progressing', 950);
  }

  function renderWalletSummary(previousState) {
    var invalid = !!(state.invalidGiftItems && state.invalidGiftItems.length);
    var panel = document.querySelector('[data-avora-wallet-panel]');
    var summary = document.querySelector('[data-avora-reward-summary]');
    if (panel) {
      var wasHidden = panel.hidden;
      panel.hidden = !state.wallet && !state.giftTotal;
      var amount = panel.querySelector('[data-avora-wallet-amount]');
      // Update both sides together so an upgrade never reads "€26 left of €20".
      if (amount) amount.textContent = money(state.wallet);
      setText(panel, '[data-avora-wallet-remaining]', money(confirmedWalletRemaining()));
      var status = panel.querySelector('[data-avora-wallet-status]');
      var cta = panel.querySelector('[data-avora-open-gifts]');
      if (status) {
        // Normal balance needs no repeated price or third explanation line.
        var walletStatus = '';
        if (invalid) walletStatus = window.AvoraI18n.text('A gift needs attention. Edit your gifts.');
        else if (!state.wallet && state.giftTotal) walletStatus = 'Add ' + money(Math.max(0, firstTierThreshold() - state.paidSubtotal)) + window.AvoraI18n.text(' in products to restore it.');
        status.textContent = walletStatus;
        status.hidden = !walletStatus;
      }
      if (cta) cta.textContent = state.giftTotal ? 'Change' : window.AvoraI18n.text('Choose gifts');
      if (!panel.hidden && wasHidden) replayMotion(panel, 'is-arriving', 900);
      if (amountIncreased(previousState, 'wallet')) replayMotion(panel, 'is-upgrading', 1250);
    }
    if (summary) {
      summary.hidden = !state.wallet && !state.giftTotal;
      setText(summary, '[data-avora-topup]', money(state.topup));
      var topupRow = summary.querySelector('[data-avora-topup-row]');
      if (topupRow) topupRow.hidden = !state.wallet || !state.topup || invalid;
    }

    var futureCardPanel = document.querySelector('[data-avora-future-card]');
    if (futureCardPanel) {
      var futureCard = state.currentTier && state.currentTier.futureCard ? state.currentTier.futureCard : 0;
      var futureMinimum = state.currentTier && state.currentTier.futureMinimum ? state.currentTier.futureMinimum : 0;
      var previousFutureCard = previousState && previousState.currentTier && previousState.currentTier.futureCard
        ? previousState.currentTier.futureCard
        : 0;
      var wasFutureCardHidden = futureCardPanel.hidden;
      futureCardPanel.hidden = !futureCard;

      if (futureCard) {
        var visualValue = futureCardPanel.querySelector('[data-avora-future-card-visual-value]');
        if (futureCard > previousFutureCard && previousFutureCard) animateMoney(visualValue, previousFutureCard, futureCard, 650);
        else if (visualValue) visualValue.textContent = money(futureCard);
        setText(futureCardPanel, '[data-avora-future-card-terms]', money(futureCard) + window.AvoraI18n.text(' off your next ') + money(futureMinimum) + '+ order');

        if (wasFutureCardHidden) replayMotion(futureCardPanel, 'is-revealed', 900);
        else if (futureCard > previousFutureCard) replayMotion(futureCardPanel, 'is-upgraded', 1050);
      }
    }
  }

  function renderCartPageRewards(previousState) {
    var panel = document.querySelector('[data-avora-cart-rewards]');
    if (!panel) return;

    panel.hidden = !state.paidCount;
    if (!state.paidCount) return;

    var progress = segmentProgress();
    var progressTrack = panel.querySelector('[data-avora-cart-reward-track]');
    var progressFill = panel.querySelector('[data-avora-cart-reward-progress]');
    var kicker = panel.querySelector('[data-avora-cart-reward-kicker]');
    var title = panel.querySelector('[data-avora-cart-reward-title]');
    var detail = panel.querySelector('[data-avora-cart-reward-detail]');
    var wallet = panel.querySelector('[data-avora-cart-reward-wallet]');
    var walletValue = panel.querySelector('[data-avora-cart-reward-wallet-value]');
    var walletStatus = panel.querySelector('[data-avora-cart-reward-wallet-status]');
    var giftButton = panel.querySelector('[data-avora-cart-reward-gifts]');
    var mapButton = panel.querySelector('[data-avora-cart-reward-map]');

    if (progressFill) progressFill.style.transform = 'scaleX(' + (progress / 100) + ')';
    if (progressTrack) {
      progressTrack.setAttribute('aria-valuenow', String(Math.round(progress)));
      progressTrack.setAttribute(
        'aria-valuetext',
        state.nextTier
          ? money(state.nextTier.threshold - state.paidSubtotal) + window.AvoraI18n.text(' away from ') + state.nextTier.title
          : window.AvoraI18n.text('Every Avora reward unlocked')
      );
    }

    if (state.nextTier) {
      var gap = state.nextTier.threshold - state.paidSubtotal;
      if (kicker) kicker.textContent = window.AvoraI18n.text('Spend another ') + money(gap) + window.AvoraI18n.text(' to get');
      if (title) title.textContent = 'A ' + money(state.nextTier.wallet) + window.AvoraI18n.text(' Gift Wallet');
      if (detail) {
        detail.textContent = state.wallet
          ? (state.nextTier.futureCard ? 'Plus a ' + money(state.nextTier.futureCard) + window.AvoraI18n.text(' Reward Card for a future order.') : window.AvoraI18n.text('Choose gifts with this order.'))
          : window.AvoraI18n.text('Choose gifts with this order.');
      }
    } else {
      if (kicker) kicker.textContent = window.AvoraI18n.text('Your reward journey');
      if (title) title.textContent = window.AvoraI18n.text('Every reward is unlocked');
      if (detail) detail.textContent = money(state.wallet) + window.AvoraI18n.text(' Gift Wallet and your future Reward Card are secured.');
    }

    panel.querySelectorAll('[data-avora-cart-reward-marker]').forEach(function (marker, index) {
      var tier = config.tiers[index];
      var threshold = tier ? tier.threshold : 0;
      if (tier) {
        marker.setAttribute('data-threshold', String(threshold));
        marker.setAttribute('aria-label', money(threshold) + ' milestone: ' + tier.title);
        var markerLabel = marker.querySelector('strong');
        if (markerLabel) markerLabel.textContent = money(threshold);
      }
      var unlocked = state.paidSubtotal >= threshold;
      var current = !!(state.nextTier && state.nextTier.threshold === threshold);
      marker.classList.toggle('is-unlocked', unlocked);
      marker.classList.toggle('is-current', current);
      if (current) marker.setAttribute('aria-current', 'step');
      else marker.removeAttribute('aria-current');
    });

    var walletVisible = !!state.wallet || !!state.giftTotal;
    if (wallet) wallet.hidden = !walletVisible;
    if (walletValue) walletValue.textContent = money(confirmedWalletRemaining()) + window.AvoraI18n.text(' left of ') + money(state.wallet);
    if (walletStatus) {
      if (state.invalidGiftItems && state.invalidGiftItems.length) {
        walletStatus.textContent = window.AvoraI18n.text('A gift needs attention. Edit your gifts.');
      } else if (!state.wallet && state.giftTotal) {
        walletStatus.textContent = 'Add ' + money(Math.max(0, firstTierThreshold() - state.paidSubtotal)) + window.AvoraI18n.text(' in products to restore your wallet.');
      } else if (!state.giftTotal) {
        walletStatus.textContent = window.AvoraI18n.text('Choose one or several gifts before checkout.');
      } else if (state.topup) {
        walletStatus.textContent = money(state.topup) + window.AvoraI18n.text(' top-up at checkout');
      } else {
        walletStatus.textContent = window.AvoraI18n.text('No top-up needed');
      }
    }
    if (giftButton) giftButton.textContent = state.giftTotal ? window.AvoraI18n.text('Edit gifts') : window.AvoraI18n.text('Choose gifts');
    if (mapButton) mapButton.hidden = walletVisible;

    if (amountIncreased(previousState, 'paidSubtotal')) replayMotion(panel, 'is-progressing', 1000);
    if (wallet && amountIncreased(previousState, 'wallet')) replayMotion(wallet, 'is-unlocked', 1250);
  }

  function renderLadder() {
    var ladder = root.querySelector('[data-avora-full-ladder]');
    if (!ladder) return;
    var nextThreshold = state.nextTier && state.nextTier.threshold;
    var currentProgress = segmentProgress();
    var unlockedCount = 0;
    ladder.innerHTML = config.tiers.map(function (tier, index) {
      var unlocked = state.paidSubtotal >= tier.threshold;
      var current = nextThreshold === tier.threshold;
      var tierProgress = unlocked ? 1 : (current ? currentProgress / 100 : 0);
      var tierState = unlocked
        ? '<span class="avora-rewards__tier-state-mark">' + config.checkmarkIcon + '</span>' + escapeHTML(window.AvoraI18n.text('Done'))
        : escapeHTML(current ? window.AvoraI18n.text('{amount} away', {amount: money(tier.threshold - state.paidSubtotal)}) : window.AvoraI18n.text('Locked'));
      var tierIcon = unlocked
        ? '<span class="avora-rewards__tier-check">' + config.checkmarkBurst + '</span>'
        : '<img src="' + escapeAttr(config.giftBoxImage) + '" width="42" height="42" alt="">';
      if (unlocked) unlockedCount += 1;
      return '<article class="avora-rewards__tier' + (unlocked ? ' is-unlocked' : '') + (current ? ' is-current' : '') + '" style="--tier-index:' + index + ';--tier-progress:' + tierProgress + '">' +
        '<span class="avora-rewards__tier-icon">' + tierIcon + '</span>' +
        '<span class="avora-rewards__tier-threshold">' + moneyShort(tier.threshold) + '</span>' +
        '<span class="avora-rewards__tier-copy"><strong>' + escapeHTML(tier.title) + '</strong><span>' + escapeHTML(tier.detail) + '</span></span>' +
        '<span class="avora-rewards__tier-state">' + tierState + '</span>' +
        '<span class="avora-rewards__tier-progress" aria-hidden="true"><span></span></span>' +
      '</article>';
    }).join('');

    renderJourneyMeter(currentProgress);

    var callout = root.querySelector('[data-avora-map-callout-copy]');
    var primary = root.querySelector('[data-avora-map-primary]');
    if (state.wallet) {
      if (callout) callout.textContent = unlockedCount + window.AvoraI18n.text(' rewards complete · ') + money(state.wallet) + window.AvoraI18n.text(' is ready in your Gift Wallet.');
      if (primary) primary.textContent = state.giftTotal ? window.AvoraI18n.text('Change my gifts') : window.AvoraI18n.text('Choose my gifts');
    } else {
      if (callout) callout.textContent = state.nextTier ? 'Add ' + money(state.nextTier.threshold - state.paidSubtotal) + window.AvoraI18n.text(' to unlock ') + state.nextTier.title + '.' : window.AvoraI18n.text('Your rewards journey is ready.');
      if (primary) primary.textContent = window.AvoraI18n.text('Continue shopping');
    }
  }

  function renderJourneyMeter(progress) {
    var track = root.querySelector('[data-avora-journey-track]');
    var fill = root.querySelector('[data-avora-journey-progress]');
    var label = root.querySelector('[data-avora-journey-label]');
    var value = root.querySelector('[data-avora-journey-value]');
    var percent = root.querySelector('[data-avora-journey-percent]');
    var copy = root.querySelector('[data-avora-journey-copy]');
    var rounded = Math.round(progress);

    if (fill) fill.style.setProperty('--journey-progress', progress / 100);
    if (track) {
      track.setAttribute('aria-valuenow', String(rounded));
      track.setAttribute('aria-valuetext', state.nextTier
        ? money(state.nextTier.threshold - state.paidSubtotal) + ' until ' + state.nextTier.title
        : window.AvoraI18n.text('All rewards unlocked'));
    }

    if (state.nextTier) {
      var previousThreshold = state.currentTier ? state.currentTier.threshold : 0;
      if (label) label.textContent = state.currentTier ? window.AvoraI18n.text('Building your next reward') : window.AvoraI18n.text('Your first reward');
      if (value) value.textContent = money(state.paidSubtotal - previousThreshold) + ' of ' + money(state.nextTier.threshold - previousThreshold);
      if (percent) percent.textContent = rounded + '%';
      if (copy) copy.textContent = money(state.nextTier.threshold - state.paidSubtotal) + ' until ' + state.nextTier.title + '.';
    } else {
      if (label) label.textContent = window.AvoraI18n.text('Journey complete');
      if (value) value.textContent = money(state.paidSubtotal) + ' spent';
      if (percent) percent.textContent = '100%';
      if (copy) copy.textContent = window.AvoraI18n.text('Every Avora reward is unlocked — beautifully done.');
    }
  }

  function animateJourneyMap(dialog) {
    if (!dialog || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    dialog.classList.remove('is-revealing');
    void dialog.offsetWidth;
    dialog.classList.add('is-revealing');
    window.setTimeout(function () { dialog.classList.remove('is-revealing'); }, 1500);
  }

  function renderCategoryFilters() {
    var list = root.querySelector('[data-avora-category-list]');
    if (!list) return;
    var counts = { all: catalog.length };
    catalog.forEach(function (product) {
      counts[product.category] = (counts[product.category] || 0) + 1;
    });
    list.innerHTML = categoryDefinitions.filter(function (category) {
      return category.id === 'all' || counts[category.id];
    }).map(function (category) {
      var selected = category.id === activeCategory;
      return '<button type="button" data-avora-category="' + escapeAttr(category.id) + '" aria-pressed="' + String(selected) + '">' +
        '<span>' + escapeHTML(category.label) + '</span>' +
      '</button>';
    }).join('');
  }

  function catalogBatchSize() {
    // Match the responsive grid: complete rows keep pagination from leaving a
    // large empty area beside the last card. Mobile starts with three rows.
    if (window.matchMedia('(max-width: 760px)').matches) return 6;
    if (window.matchMedia('(max-width: 900px)').matches) return 6;
    if (window.matchMedia('(max-width: 1020px)').matches) return 8;
    if (window.matchMedia('(max-width: 1399px)').matches) return 10;
    return 12;
  }

  function resetCatalogRenderLimit() {
    catalogRenderLimit = catalogBatchSize();
    pendingCatalogFocusIndex = -1;
  }

  function showMoreCatalog(button) {
    var currentCount = Number(button && button.dataset.visibleCount) || catalogRenderLimit || catalogBatchSize();
    pendingCatalogFocusIndex = currentCount;
    catalogRenderLimit = currentCount + catalogBatchSize();
    renderGiftGrid();
  }

  function syncCatalogControls(resultCount) {
    renderCategoryFilters();
    var clear = root.querySelector('[data-avora-search-clear]');
    if (clear) clear.hidden = !searchQuery;
    var filterLabel = root.querySelector('[data-avora-wallet-filter-label]');
    if (filterLabel) filterLabel.textContent = filterWithinWallet ? window.AvoraI18n.text('Wallet-fit only') : window.AvoraI18n.text('Fits my wallet');
    var filter = root.querySelector('[data-avora-picker-filter]');
    if (filter) filter.setAttribute('aria-pressed', String(filterWithinWallet));
    var sortLabel = root.querySelector('[data-avora-sort-label]');
    var selectedSort = root.querySelector('[data-avora-sort-option="' + escapeSelector(sortMode) + '"]');
    if (sortLabel && selectedSort) sortLabel.textContent = selectedSort.textContent;
    root.querySelectorAll('[data-avora-sort-option]').forEach(function (option) {
      option.setAttribute('aria-selected', String(option.dataset.avoraSortOption === sortMode));
    });
  }

  function renderGiftGrid() {
    var grid = root.querySelector('[data-avora-gift-grid]');
    if (!grid) return;
    if (activeVariantDropdown) closeVariantDropdown(false);
    var previousScroll = grid.scrollTop;
    var focusedCard = document.activeElement && document.activeElement.closest && document.activeElement.closest('[data-avora-gift-card]');
    var focusedHandle = focusedCard && focusedCard.dataset.productHandle;
    var focusedControl = document.activeElement && document.activeElement.dataset ? document.activeElement.dataset.avoraFocusKey : '';
    var selections = giftSelectionsByVariant();
    var canIncreaseGift = core.canIncreaseGift(state) && !giftMutationPending;
    var matchingProducts = rankCatalog().filter(function (product) {
      var selectedQuantity = productGiftQuantity(product, selections);
      var hasWalletFit = product.variants.some(function (variant) {
        return variant.available && state.giftTotal + variant.price <= state.wallet;
      });
      return !filterWithinWallet || selectedQuantity || hasWalletFit;
    });
    syncCatalogControls(matchingProducts.length);

    if (!matchingProducts.length) {
      grid.innerHTML = catalogLoading
        ? window.AvoraI18n.fragment('<div class="avora-rewards__empty-picker is-loading"><span class="avora-rewards__catalog-loader" aria-hidden="true"></span><strong>Finding every Avora product</strong><span>Your complete gift shop is loading now.</span></div>')
        : catalogLoadError
        ? '<div class="avora-rewards__empty-picker" role="status"><strong>' + escapeHTML(window.AvoraI18n.text('Gifts couldn’t load')) + '</strong><span>' + escapeHTML(window.AvoraI18n.text('Please try again. Your cart and gifts are saved.')) + '</span><button type="button" data-avora-catalog-retry>' + escapeHTML(window.AvoraI18n.text('Try again')) + '</button></div>'
        : window.AvoraI18n.fragment('<div class="avora-rewards__empty-picker"><strong>No products match these filters.</strong><span>Try another search or see the full Avora collection.</span><button type="button" data-avora-filter-reset>Reset search &amp; filters</button></div>');
      return;
    }

    if (!catalogRenderLimit) resetCatalogRenderLimit();
    var products = matchingProducts.slice(0, catalogRenderLimit);
    var hasMoreProducts = products.length < matchingProducts.length;

    grid.innerHTML = products.map(function (product, index) {
      var variant = selectedVariantFor(product, selections);
      var selected = selections[String(variant.id)];
      var productSelectedQuantity = productGiftQuantity(product, selections);
      var images = product.images.length ? product.images : [{ src: product.image || config.giftBoxImage, alt: product.title }];
      var imageIndex = Math.max(0, Math.min(Number(imageIndexes[product.handle]) || 0, images.length - 1));
      imageIndexes[product.handle] = imageIndex;
      var image = images[imageIndex];
      var productHref = product.url + (product.url.indexOf('?') === -1 ? '?' : '&') + 'variant=' + variant.id;
      var remaining = confirmedWalletRemaining();
      var incrementalTopup = Math.max(0, variant.price - remaining);
      var coverage = selected
        ? (selected.quantity === 1 ? window.AvoraI18n.text('Added to your gifts') : selected.quantity + ' gifts · ' + money(selected.lineTotal) + ' total')
        : (!variant.available ? window.AvoraI18n.text('This option is sold out') : (!state.wallet ? window.AvoraI18n.text('Gift Wallet not unlocked')
          : (remaining < 1 ? window.AvoraI18n.text('Wallet fully used')
          : (incrementalTopup > 0 ? money(incrementalTopup) + window.AvoraI18n.text(' top-up for this gift') : money(remaining - variant.price) + window.AvoraI18n.text(' left after adding')))));
      var hasVariants = product.variants.length > 1;
      var optionName = (product.optionNames || []).filter(function (name) { return name && name !== 'Title'; }).join(' / ') || 'option';
      var safeHandle = escapeAttr(product.handle);
      var selectedLabel = variant.title && variant.title !== 'Default Title' ? variant.title : window.AvoraI18n.text('Selected gift');
      var variantName = variantDisplayName(variant);
      var variantTriggerId = 'avora-gift-variant-' + safeHandle;
      var variantListboxId = variantTriggerId + '-options';
      return '<article class="avora-rewards__gift-card' + (productSelectedQuantity ? ' has-selection' : '') + (selected ? ' is-selected' : '') + '" data-avora-gift-card data-product-handle="' + safeHandle + '">' +
        '<div class="avora-rewards__gift-card-media" data-avora-gift-media>' +
          (productSelectedQuantity ? '<span class="avora-rewards__gift-status">' + productSelectedQuantity + ' selected</span>' : '') +
          '<a class="avora-rewards__gift-media-link" href="' + escapeAttr(productHref) + '" data-avora-product-link data-avora-focus-key="details" aria-label="View ' + escapeAttr(product.title) + ' product details">' +
            '<img src="' + escapeAttr(catalogueImageUrl(image.src || config.giftBoxImage)) + '" srcset="' + escapeAttr(catalogueImageSrcset(image.src)) + '" sizes="(max-width: 760px) calc((100vw - 54px) / 2), (max-width: 900px) calc((100vw - 110px) / 3), (max-width: 1020px) calc((100vw - 140px) / 4), (max-width: 1399px) calc((100vw - 170px) / 5), (max-width: 1496px) calc((100vw - 200px) / 6), 216px" width="520" height="520" alt="' + escapeAttr(image.alt || product.title) + '" loading="' + (index < 6 ? 'eager' : 'lazy') + '" decoding="async" data-avora-gift-image>' +
          '</a>' +
          (images.length > 1 ?
            '<button class="avora-rewards__image-arrow is-prev" type="button" data-avora-image-prev aria-label="Previous image of ' + escapeAttr(product.title) + '"><img src="' + escapeAttr(config.chevronLeft) + '" width="16" height="16" alt=""></button>' +
            '<button class="avora-rewards__image-arrow is-next" type="button" data-avora-image-next aria-label="Next image of ' + escapeAttr(product.title) + '"><img src="' + escapeAttr(config.chevronRight) + '" width="16" height="16" alt=""></button>' +
            '<span class="avora-rewards__image-count" data-avora-image-count>' + (imageIndex + 1) + ' / ' + images.length + '</span>' +
            '<span class="visually-hidden" data-avora-image-status aria-live="polite">Image ' + (imageIndex + 1) + ' of ' + images.length + '</span>'
          : '') +
        '</div>' +
        '<div class="avora-rewards__gift-card-controls">' +
          '<span class="avora-rewards__gift-card-category">' + escapeHTML(categoryLabel(product.category)) + '</span>' +
          '<div class="avora-rewards__gift-card-summary">' +
            '<span class="avora-rewards__gift-card-title" title="' + escapeAttr(product.title) + '">' + escapeHTML(product.title) + '</span>' +
            '<strong class="avora-rewards__gift-card-price">' + money(variant.price) + '</strong>' +
          '</div>' +
          '<div class="avora-rewards__gift-card-meta">' +
            '<span class="avora-rewards__gift-card-coverage' + (incrementalTopup > 0 && !selected && remaining > 0 ? ' is-topup' : '') + '">' + escapeHTML(coverage) + '</span>' +
          '</div>' +
          (hasVariants ?
            '<div class="avora-rewards__variant-field">' +
              '<span>Choose ' + escapeHTML(optionName) + '</span>' +
              '<span class="avora-rewards__variant-control">' +
                '<button class="avora-rewards__variant-trigger" id="' + variantTriggerId + '" type="button" data-avora-variant-trigger data-avora-focus-key="variant" data-product-handle="' + safeHandle + '" data-variant-id="' + variant.id + '" data-variant-count="' + product.variants.length + '" data-option-name="' + escapeAttr(optionName) + '" aria-label="Choose ' + escapeAttr(optionName) + ' for ' + escapeAttr(product.title) + '. Currently ' + escapeAttr(variantName) + ', ' + escapeAttr(money(variant.price)) + '" aria-haspopup="listbox" aria-expanded="false" aria-controls="' + variantListboxId + '">' +
                  '<span class="avora-rewards__variant-trigger-copy"><strong>' + escapeHTML(variantName) + '</strong></span>' +
                  '<span class="avora-rewards__variant-trigger-caret" aria-hidden="true">' + config.caretIcon + '</span>' +
                '</button>' +
              '</span>' +
            '</div>' : '') +
          (selected ?
            '<div class="avora-rewards__gift-selection">' +
              '<span class="avora-rewards__gift-selection-copy"><span>' + escapeHTML(selectedLabel) + '</span><strong>' + selected.quantity + ' in your gifts</strong></span>' +
              '<div class="avora-rewards__gift-selection-actions">' +
                '<div class="avora-rewards__gift-quantity" role="group" aria-label="Quantity of ' + escapeAttr(product.title) + ', ' + escapeAttr(selectedLabel) + '">' +
                  '<button type="button" data-avora-gift-quantity data-delta="-1" data-line-key="' + escapeAttr(selected.primaryKey) + '" data-quantity="' + selected.quantity + '" data-variant-id="' + variant.id + '" data-avora-focus-key="decrease" aria-label="' + (selected.quantity === 1 ? 'Remove ' : window.AvoraI18n.text('Decrease quantity of ')) + escapeAttr(product.title) + ', ' + escapeAttr(selectedLabel) + '">' + config.minusIcon + '</button>' +
                  '<span aria-live="polite" aria-label="Quantity ' + selected.quantity + '">' + selected.quantity + '</span>' +
                  '<button type="button" data-avora-gift-quantity data-delta="1" data-line-key="' + escapeAttr(selected.primaryKey) + '" data-quantity="' + selected.quantity + '" data-variant-id="' + variant.id + '" data-avora-focus-key="increase" aria-label="Increase quantity of ' + escapeAttr(product.title) + ', ' + escapeAttr(selectedLabel) + '"' + (!canIncreaseGift || !variant.available ? ' disabled' : '') + '>' + config.plusIcon + '</button>' +
                '</div>' +
                '<button class="avora-rewards__gift-remove" type="button" data-avora-gift-action data-variant-id="' + variant.id + '" data-line-key="' + escapeAttr(selected.primaryKey) + window.AvoraI18n.fragment('" data-avora-focus-key="remove">Remove</button>') +
              '</div>' +
            '</div>' :
            '<button class="avora-rewards__gift-action" type="button" data-avora-gift-action data-variant-id="' + variant.id + '" data-avora-focus-key="add"' + (!variant.available || variant.price < 1 || !canIncreaseGift ? ' disabled' : '') + '>' +
              '<svg aria-hidden="true" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M10 4v12M4 10h12"/></svg><span>' + escapeHTML(window.AvoraI18n.text('Add gift')) + '</span>' +
            '</button>') +
        '</div>' +
      '</article>';
    }).join('') + (hasMoreProducts
      ? '<div class="avora-rewards__catalog-more" data-avora-catalog-pagination>' +
          '<span><strong>' + products.length + '</strong> of ' + matchingProducts.length + ' products ready</span>' +
          '<button type="button" data-avora-catalog-more data-visible-count="' + products.length + '">' +
            '<span>Show ' + Math.min(catalogBatchSize(), matchingProducts.length - products.length) + ' more</span>' +
            '<span aria-hidden="true">' + config.caretIcon + '</span>' +
          '</button>' +
        '</div>'
      : '');

    // Filtering or changing variants can rebuild cards while a cart write is
    // pending. Keep every mutation control locked in that fresh markup too.
    if (giftMutationPending) {
      grid.querySelectorAll('[data-avora-gift-action], [data-avora-gift-quantity]').forEach(function (control) { control.disabled = true; });
    }
    observeGiftGalleries(grid);
    updatePickerTotals();
    window.requestAnimationFrame(function () {
      grid.scrollTop = previousScroll;
      if (pendingCatalogFocusIndex >= 0) {
        var newlyVisibleCard = grid.querySelectorAll('[data-avora-gift-card]')[pendingCatalogFocusIndex];
        var newlyVisibleLink = newlyVisibleCard && newlyVisibleCard.querySelector('[data-avora-product-link]');
        pendingCatalogFocusIndex = -1;
        if (newlyVisibleLink) newlyVisibleLink.focus({ preventScroll: true });
        return;
      }
      if (!focusedHandle || !focusedControl || !activeDialog) return;
      var restoredCard = grid.querySelector('[data-product-handle="' + escapeSelector(focusedHandle) + '"]');
      var restoredControl = restoredCard && restoredCard.querySelector('[data-avora-focus-key="' + escapeSelector(focusedControl) + '"]');
      if (restoredControl && restoredControl.disabled) restoredControl = null;
      if (!restoredControl && restoredCard && (focusedControl === 'add' || focusedControl === 'decrease' || focusedControl === 'remove')) {
        restoredControl = restoredCard.querySelector('[data-avora-focus-key="increase"]')
          || restoredCard.querySelector('[data-avora-focus-key="add"]')
          || restoredCard.querySelector('[data-avora-focus-key="variant"]');
      }
      if (restoredControl) restoredControl.focus({ preventScroll: true });
    });
  }

  function giftSelectionsByVariant() {
    var selections = {};
    state.giftItems.forEach(function (item) {
      var variantId = String(item.variant_id);
      if (!selections[variantId]) {
        selections[variantId] = { quantity: 0, lineTotal: 0, items: [], primaryKey: item.key };
      }
      selections[variantId].quantity += Number(item.quantity) || 0;
      selections[variantId].lineTotal += core.rewardGiftLineValue(item, config.codes);
      selections[variantId].items.push(item);
    });
    return selections;
  }

  function productGiftQuantity(product, selections) {
    return product.variants.reduce(function (total, variant) {
      var selection = selections[String(variant.id)];
      return total + (selection ? selection.quantity : 0);
    }, 0);
  }

  function findVariant(product, variantId) {
    if (!product || !Array.isArray(product.variants)) return null;
    for (var i = 0; i < product.variants.length; i += 1) {
      if (String(product.variants[i].id) === String(variantId)) return product.variants[i];
    }
    return null;
  }

  function variantDisplayName(variant) {
    return variant && variant.title && variant.title !== 'Default Title' ? variant.title : 'Standard';
  }

  function toggleVariantDropdown(trigger) {
    if (activeVariantDropdown && activeVariantDropdown.trigger === trigger) {
      closeVariantDropdown(true);
      return;
    }
    openVariantDropdown(trigger, 'selected');
  }

  function openVariantDropdown(trigger, focusTarget) {
    var card = trigger && trigger.closest('[data-avora-gift-card]');
    var product = card && findProduct(card.dataset.productHandle);
    if (!trigger || !product) return;
    closeVariantDropdown(false);

    var selections = giftSelectionsByVariant();
    var selectedId = String(trigger.dataset.variantId || '');
    var optionName = trigger.dataset.optionName || 'option';
    var listboxId = trigger.getAttribute('aria-controls');
    var options = product.variants.map(function (variant, index) {
      var selection = selections[String(variant.id)];
      var selected = String(variant.id) === selectedId;
      var unavailable = !variant.available && !selection;
      var meta = [money(variant.price)];
      if (selection) meta.push(selection.quantity + window.AvoraI18n.text(' in your gifts'));
      if (unavailable) meta.push(window.AvoraI18n.text('Sold out'));
      return '<button class="avora-rewards__variant-option" id="' + escapeAttr(listboxId) + '-option-' + index + '" type="button" role="option" data-avora-variant-option data-product-handle="' + escapeAttr(product.handle) + '" data-variant-id="' + variant.id + '" aria-selected="' + String(selected) + '"' + (unavailable ? ' aria-disabled="true" disabled' : '') + '>' +
        '<span class="avora-rewards__variant-option-mark" aria-hidden="true">' + (selected ? config.checkmarkIcon : '') + '</span>' +
        '<span class="avora-rewards__variant-option-copy"><strong>' + escapeHTML(variantDisplayName(variant)) + '</strong><small>' + escapeHTML(meta.join(' · ')) + '</small></span>' +
        (selected ? window.AvoraI18n.fragment('<span class="avora-rewards__variant-option-state">Selected</span>') : '') +
      '</button>';
    }).join('');

    var popover = document.createElement('div');
    popover.className = 'avora-rewards__variant-popover';
    popover.dataset.avoraVariantPopover = '';
    popover.innerHTML = '<button class="avora-rewards__variant-backdrop" type="button" data-avora-variant-backdrop tabindex="-1" aria-label="Close ' + escapeAttr(optionName) + ' options"></button>' +
      '<div class="avora-rewards__variant-panel" role="listbox" id="' + escapeAttr(listboxId) + '" aria-label="Choose ' + escapeAttr(optionName) + ' for ' + escapeAttr(product.title) + '">' +
        '<div class="avora-rewards__variant-panel-head"><span><small>Choose ' + escapeHTML(optionName) + '</small><strong>' + escapeHTML(product.title) + window.AvoraI18n.fragment('</strong></span><button type="button" data-avora-variant-close>Done</button></div>') +
        '<div class="avora-rewards__variant-options">' + options + '</div>' +
      '</div>';

    root.appendChild(popover);
    trigger.setAttribute('aria-expanded', 'true');
    card.classList.add('is-choosing-variant');
    activeVariantDropdown = { popover: popover, trigger: trigger, card: card, product: product };
    positionVariantDropdown();
    window.requestAnimationFrame(function () {
      if (!activeVariantDropdown || activeVariantDropdown.popover !== popover) return;
      popover.classList.add('is-open');
      var enabled = popover.querySelectorAll('[data-avora-variant-option]:not([disabled])');
      var target = focusTarget === 'last'
        ? enabled[enabled.length - 1]
        : popover.querySelector('[data-avora-variant-option][aria-selected="true"]') || enabled[0];
      if (target) {
        target.focus();
        scrollVariantOptionIntoView(target, focusTarget === 'selected' ? 'center' : 'nearest');
      }
    });
  }

  function chooseGiftVariant(option) {
    var product = findProduct(option && option.dataset.productHandle);
    var variant = product && findVariant(product, option.dataset.variantId);
    if (!product || !variant || option.disabled) return;
    variantSelections[product.handle] = String(variant.id);
    selectProductVariantImage(product, variant);
    updateStoredPickerState();
    closeVariantDropdown(false);
    renderGiftGrid();
  }

  function closeVariantDropdown(restoreFocus) {
    if (!activeVariantDropdown) return;
    var dropdown = activeVariantDropdown;
    activeVariantDropdown = null;
    if (dropdown.trigger) dropdown.trigger.setAttribute('aria-expanded', 'false');
    if (dropdown.card) dropdown.card.classList.remove('is-choosing-variant');
    if (dropdown.popover && dropdown.popover.parentNode) dropdown.popover.parentNode.removeChild(dropdown.popover);
    if (restoreFocus !== false && dropdown.trigger && document.contains(dropdown.trigger)) dropdown.trigger.focus({ preventScroll: true });
  }

  function positionVariantDropdown() {
    if (!activeVariantDropdown) return;
    var dropdown = activeVariantDropdown;
    var panel = dropdown.popover.querySelector('.avora-rewards__variant-panel');
    var trigger = dropdown.trigger;
    if (!panel || !trigger || !document.contains(trigger)) {
      closeVariantDropdown(false);
      return;
    }

    var mobileSheet = window.matchMedia('(max-width: 760px), (max-height: 540px) and (pointer: coarse)').matches;
    dropdown.popover.classList.toggle('is-mobile-sheet', mobileSheet);
    panel.removeAttribute('style');
    if (mobileSheet) return;

    var rect = trigger.getBoundingClientRect();
    var viewportWidth = document.documentElement.clientWidth;
    var viewportHeight = document.documentElement.clientHeight;
    var width = Math.min(Math.max(rect.width, 270), viewportWidth - 24);
    var left = Math.max(12, Math.min(rect.left, viewportWidth - width - 12));
    var spaceBelow = viewportHeight - rect.bottom - 16;
    var spaceAbove = rect.top - 16;
    var opensAbove = spaceBelow < 260 && spaceAbove > spaceBelow;
    var available = Math.max(150, (opensAbove ? spaceAbove : spaceBelow) - 8);
    panel.style.width = width + 'px';
    panel.style.maxHeight = Math.min(420, available, Math.max(120, viewportHeight - 24)) + 'px';
    panel.style.left = left + 'px';
    var panelHeight = panel.getBoundingClientRect().height;
    var preferredTop = opensAbove ? rect.top - panelHeight - 8 : rect.bottom + 8;
    var maxTop = Math.max(12, viewportHeight - panelHeight - 12);
    panel.style.top = Math.max(12, Math.min(preferredTop, maxTop)) + 'px';
    dropdown.popover.dataset.placement = opensAbove ? 'top' : 'bottom';
  }

  function scrollVariantOptionIntoView(option, alignment) {
    var list = option && option.closest('.avora-rewards__variant-options');
    if (!list || list.scrollHeight <= list.clientHeight) return;
    var edgePadding = 5;
    var listRect = list.getBoundingClientRect();
    var optionRect = option.getBoundingClientRect();
    var nextScroll = list.scrollTop;
    if (alignment === 'center') {
      nextScroll += (optionRect.top + (optionRect.height / 2)) -
        (listRect.top + (listRect.height / 2));
    } else if (optionRect.top < listRect.top + edgePadding) {
      nextScroll -= (listRect.top + edgePadding) - optionRect.top;
    } else if (optionRect.bottom > listRect.bottom - edgePadding) {
      nextScroll += optionRect.bottom - (listRect.bottom - edgePadding);
    }
    var scrollTarget = Math.max(0, Math.min(nextScroll, list.scrollHeight - list.clientHeight));
    if (typeof list.scrollTo === 'function') list.scrollTo({ top: scrollTarget, behavior: 'auto' });
    else list.scrollTop = scrollTarget;
  }

  function handleVariantDropdownKeydown(event) {
    var trigger = event.target.closest && event.target.closest('[data-avora-variant-trigger]');
    if (trigger && !activeVariantDropdown && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      openVariantDropdown(trigger, event.key === 'ArrowUp' ? 'last' : 'selected');
      return true;
    }
    if (!activeVariantDropdown) return false;

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeVariantDropdown(true);
      return true;
    }

    var popover = activeVariantDropdown.popover;
    var option = event.target.closest && event.target.closest('[data-avora-variant-option]');
    var enabledOptions = Array.prototype.slice.call(popover.querySelectorAll('[data-avora-variant-option]:not([disabled])'));
    if (option && ['ArrowDown', 'ArrowUp', 'Home', 'End'].indexOf(event.key) !== -1) {
      event.preventDefault();
      var index = enabledOptions.indexOf(option);
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = enabledOptions.length - 1;
      else index = (index + (event.key === 'ArrowDown' ? 1 : -1) + enabledOptions.length) % enabledOptions.length;
      if (enabledOptions[index]) {
        var nextOption = enabledOptions[index];
        nextOption.focus();
        window.requestAnimationFrame(function () {
          scrollVariantOptionIntoView(nextOption, 'nearest');
        });
      }
      return true;
    }

    if (event.key === 'Tab') {
      var focusables = enabledOptions.concat(Array.prototype.slice.call(popover.querySelectorAll('[data-avora-variant-close]')));
      if (!focusables.length) return true;
      event.preventDefault();
      var current = focusables.indexOf(document.activeElement);
      var next = event.shiftKey ? current - 1 : current + 1;
      if (next < 0) next = focusables.length - 1;
      if (next >= focusables.length) next = 0;
      focusables[next].focus();
      return true;
    }
    return false;
  }

  function selectedVariantFor(product, selections) {
    var saved = findVariant(product, variantSelections[product.handle]);
    if (saved && (saved.available || selections[String(saved.id)])) return saved;
    for (var i = 0; i < product.variants.length; i += 1) {
      if (selections[String(product.variants[i].id)]) {
        variantSelections[product.handle] = String(product.variants[i].id);
        return product.variants[i];
      }
    }
    var fallback = findVariant(product, product.variantId) || product.variants.filter(function (variant) { return variant.available; })[0];
    variantSelections[product.handle] = String(fallback.id);
    return fallback;
  }

  function selectGiftVariant(select) {
    var card = select.closest('[data-avora-gift-card]');
    var product = card && findProduct(card.dataset.productHandle);
    var variant = product && findVariant(product, select.value);
    if (!product || !variant) return;
    variantSelections[product.handle] = String(variant.id);
    selectProductVariantImage(product, variant);
    updateStoredPickerState();
    renderGiftGrid();
  }

  function selectProductVariantImage(product, variant) {
    if (variant.image && product.images && product.images.length) {
      for (var i = 0; i < product.images.length; i += 1) {
        if (giftImageKey(product.images[i].src) === giftImageKey(variant.image)) {
          imageIndexes[product.handle] = i;
          return;
        }
      }
    }
  }

  function changeGiftImage(button) {
    var card = button.closest('[data-avora-gift-card]');
    setGiftImage(card, button.matches('[data-avora-image-next]') ? 1 : -1, true);
  }

  function prepareGiftImage(card, product, index) {
    var node=card.querySelector('[data-avora-gift-image]');
    var key=index+':'+Math.ceil(node.clientWidth*(window.devicePixelRatio || 1));
    var cache=card._giftImageCache || (card._giftImageCache={});
    if (cache[key]) return cache[key];
    var image=new Image();
    var entry=cache[key]={image:image,ready:false};
    entry.promise=new Promise(function(resolve) {
      var settled=false;
      var timeout=window.setTimeout(function(){finish(false);},12000);
      function finish(ok) {
        if (settled) return;
        settled=true;
        window.clearTimeout(timeout);
        image.onload=image.onerror=null;
        entry.ready=ok;
        if (!ok) delete cache[key];
        resolve(ok);
      }
      image.onload=function() {
        if (image.decode) image.decode().then(function(){finish(true);},function(){finish(image.naturalWidth>0);});
        else finish(true);
      };
      image.onerror=function(){finish(false);};
      image.sizes=node.sizes;
      image.srcset=catalogueImageSrcset(product.images[index].src);
      image.src=catalogueImageUrl(product.images[index].src);
    });
    return entry;
  }

  function warmGiftGallery(card) {
    var product=findProduct(card.dataset.productHandle);
    if (!product || product.images.length<2) return;
    var index=Number(imageIndexes[product.handle]) || 0, count=product.images.length;
    prepareGiftImage(card,product,(index+1)%count);
    prepareGiftImage(card,product,(index+count-1)%count);
  }

  var giftGalleryObserver;
  function observeGiftGalleries(grid) {
    if (!('IntersectionObserver' in window)) return;
    if (giftGalleryObserver) giftGalleryObserver.disconnect();
    giftGalleryObserver=new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        warmGiftGallery(entry.target);
        giftGalleryObserver.unobserve(entry.target);
      });
    },{root:grid,rootMargin:'160px'});
    grid.querySelectorAll('[data-avora-gift-card]').forEach(function(card) {giftGalleryObserver.observe(card);});
  }

  function setGiftImage(card, direction, announce) {
    if (!card) return;
    var product=findProduct(card.dataset.productHandle);
    if (!product || !product.images || product.images.length<2) return;
    var current=card._giftPendingIndex != null ? card._giftPendingIndex : Number(imageIndexes[product.handle]) || 0;
    var next=(current+direction+product.images.length)%product.images.length;
    card._giftPendingIndex=next;
    var request=(card._giftImageRequest || 0)+1;
    card._giftImageRequest=request;
    var entry=prepareGiftImage(card,product,next);
    var media=card.querySelector('[data-avora-gift-media]');
    function show(ok) {
      if (request!==card._giftImageRequest || !card.isConnected) return;
      card._giftPendingIndex=null;
      media.removeAttribute('aria-busy');
      // Retain the current photo and count on failure, allowing the same click to retry.
      if (!ok) return;
      var imageNode=card.querySelector('[data-avora-gift-image]');
      if (card._giftImageTransition) {card._giftImageTransition.cancel();card._giftImageTransition=null;}
      var old=media.querySelector('[data-gift-image-outgoing]');
      if (old) old.remove();
      var animate=imageNode.animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (animate) {
        old=imageNode.cloneNode();old.removeAttribute('data-avora-gift-image');old.removeAttribute('id');
        old.alt='';old.setAttribute('aria-hidden','true');old.setAttribute('data-gift-image-outgoing','');old.loading='eager';
        media.append(old);
      }
      imageNode.loading='eager';
      imageNode.srcset=entry.image.srcset;
      imageNode.src=entry.image.src;
      imageNode.alt=product.images[next].alt || product.title;
      if (animate) {
        var transition=old.animate([{opacity:1},{opacity:0}],{duration:180,easing:'cubic-bezier(.25,.1,.25,1)'});
        card._giftImageTransition=transition;
        transition.onfinish=function(){old.remove();if(card._giftImageTransition===transition)card._giftImageTransition=null;};
      }
      imageIndexes[product.handle]=next;
      var count=card.querySelector('[data-avora-image-count]');
      var status=card.querySelector('[data-avora-image-status]');
      if (count) count.textContent=(next+1)+' / '+product.images.length;
      if (status && announce) status.textContent='Image '+(next+1)+' of '+product.images.length;
      updateStoredPickerState();
      warmGiftGallery(card);
    }
    if (entry.ready) show(true);
    else {media.setAttribute('aria-busy','true');entry.promise.then(show);}
  }

  function findProduct(handle) {
    for (var i = 0; i < catalog.length; i += 1) {
      if (catalog[i].handle === handle) return catalog[i];
    }
    return null;
  }

  function rankCatalog() {
    var cartHandles = {};
    var searchScores = {};
    var selections = giftSelectionsByVariant();
    cart.items.forEach(function (item) {
      if (!isRewardGift(item) && item.handle) cartHandles[item.handle] = true;
    });
    return catalog.filter(function (product) {
      if (activeCategory !== 'all' && product.category !== activeCategory) return false;
      var searchScore = core.productSearchScore(product, searchQuery);
      if (searchScore < 0) return false;
      searchScores[product.handle] = searchScore;
      return true;
    }).sort(function (a, b) {
      var aSelected = productGiftQuantity(a, selections) ? 1 : 0;
      var bSelected = productGiftQuantity(b, selections) ? 1 : 0;
      if (aSelected !== bSelected) return bSelected - aSelected;
      if (searchQuery && sortMode === 'recommended' && searchScores[a.handle] !== searchScores[b.handle]) {
        return searchScores[a.handle] - searchScores[b.handle];
      }
      var aMinPrice = minAvailablePrice(a);
      var bMinPrice = minAvailablePrice(b);
      if (sortMode === 'price-asc') return aMinPrice - bMinPrice || a.title.localeCompare(b.title);
      if (sortMode === 'price-desc') return bMinPrice - aMinPrice || a.title.localeCompare(b.title);
      if (sortMode === 'title') return a.title.localeCompare(b.title);
      var aScore = cartHandles[a.handle] ? -100 : (aMinPrice <= Math.max(0, state.wallet - state.giftTotal) ? 20 : 0);
      var bScore = cartHandles[b.handle] ? -100 : (bMinPrice <= Math.max(0, state.wallet - state.giftTotal) ? 20 : 0);
      return bScore - aScore || aMinPrice - bMinPrice || a.title.localeCompare(b.title);
    });
  }

  function minAvailablePrice(product) {
    var prices = product.variants.filter(function (variant) { return variant.available; }).map(function (variant) { return variant.price; });
    return prices.length ? Math.min.apply(null, prices) : Number.MAX_SAFE_INTEGER;
  }

  function updatePickerTotals() {
    if (!root) return;
    // Older cached section HTML may still carry the duplicated remaining balance.
    var footerSummary = root.querySelector('.avora-rewards__picker-wallet-summary');
    if (footerSummary && footerSummary.querySelector && !footerSummary.querySelector('[data-avora-picker-used]')) {
      footerSummary.innerHTML = '<span class="avora-rewards__picker-total-label">'+escapeHTML(window.AvoraI18n.text('Gift Wallet used'))+'</span><strong data-avora-picker-used></strong>';
    }
    var remaining = confirmedWalletRemaining();
    var giftCount = (state.giftItems || []).reduce(function (count, item) { return count + (Number(item.quantity) || 0); }, 0);
    var invalid = !!(state.invalidGiftItems && state.invalidGiftItems.length);
    root.querySelectorAll('[data-avora-picker-wallet]').forEach(function (node) { node.textContent = money(state.wallet || 0); });
    root.querySelectorAll('[data-avora-picker-remaining]').forEach(function (node) { node.textContent = money(remaining); });
    setText(root, '[data-avora-picker-count]', giftCount ? giftCount + (giftCount === 1 ? window.AvoraI18n.text(' gift selected') : window.AvoraI18n.text(' gifts selected')) : window.AvoraI18n.text('No gifts selected'));
    setText(root, '[data-avora-picker-topup]', money(state.topup));
    setText(root, '[data-avora-picker-used]', money(Math.max(0, Math.min(state.wallet || 0, state.appliedCredit || 0))));
    var topupRow = root.querySelector('[data-avora-picker-topup-row]');
    var covered = root.querySelector('[data-avora-picker-covered]');
    // A paused/invalid wallet needs recovery, not a misleading full-price top-up.
    var hasTopup = state.wallet > 0 && state.topup > 0 && !invalid;
    if (topupRow) topupRow.hidden = !hasTopup;
    if (covered) {
      covered.hidden = hasTopup;
      covered.textContent = invalid ? window.AvoraI18n.text('Check your gifts') : (state.wallet ? window.AvoraI18n.text('No top-up needed') : window.AvoraI18n.text('Wallet not unlocked'));
    }
    var progress = root.querySelector('[data-avora-picker-progress]');
    var track = root.querySelector('[data-avora-picker-track]');
    var message = root.querySelector('[data-avora-picker-message]');
    var fraction = state.wallet ? Math.max(0, Math.min(1, remaining / state.wallet)) : 0;
    if (progress) {
      progress.style.transform = 'scaleX(' + fraction + ')';
    }
    if (track) {
      track.setAttribute('style', '--wallet-left:' + (fraction * 100) + '%');
      track.setAttribute('aria-valuenow', String(Math.round(fraction * 100)));
      track.setAttribute('aria-valuetext', money(remaining) + window.AvoraI18n.text(' left of your ') + money(state.wallet || 0) + window.AvoraI18n.text(' Gift Wallet'));
    }
    if (message) {
      if (giftMutationPending) message.textContent = window.AvoraI18n.text('Updating your gifts…');
      else if (invalid) message.textContent = window.AvoraI18n.text('A gift needs attention. Remove it and choose it again.');
      else if (!state.wallet) message.textContent = 'Add ' + money(Math.max(0, firstTierThreshold() - (state.paidSubtotal || 0))) + window.AvoraI18n.text(' in paid products to unlock your Gift Wallet.');
      else if (!giftCount) message.textContent = window.AvoraI18n.text('For this order. Any top-up is shown before you add.');
      else if (!remaining) message.textContent = window.AvoraI18n.text('Wallet fully used. Remove or reduce a gift to make room.');
      else message.textContent = window.AvoraI18n.text('Gifts are saved as you choose. Keep choosing or review your cart.');
    }
    var done = root.querySelector('[data-avora-picker-done]');
    if (done) {
      done.disabled = giftMutationPending;
      done.setAttribute('aria-busy', String(giftMutationPending));
      done.textContent = giftMutationPending ? window.AvoraI18n.text('Updating gifts…') : (giftCount ? window.AvoraI18n.text('Review gifts in cart') : window.AvoraI18n.text('Back to cart'));
    }
  }

  function toggleFilter(button) {
    filterWithinWallet = !filterWithinWallet;
    button.setAttribute('aria-pressed', String(filterWithinWallet));
    var label = button.querySelector('[data-avora-wallet-filter-label]');
    if (label) label.textContent = filterWithinWallet ? window.AvoraI18n.text('Wallet-fit only') : window.AvoraI18n.text('Fits my wallet');
    resetCatalogRenderLimit();
    updateStoredPickerState();
    renderGiftGrid();
  }

  function chooseCategory(button) {
    activeCategory = button.dataset.avoraCategory || 'all';
    resetCatalogRenderLimit();
    updateStoredPickerState();
    renderGiftGrid();
    var grid = root.querySelector('[data-avora-gift-grid]');
    if (grid) grid.scrollTop = 0;
  }

  function clearGiftSearch() {
    var search = root.querySelector('[data-avora-gift-search]');
    searchQuery = '';
    resetCatalogRenderLimit();
    if (search) {
      search.value = '';
      search.focus({ preventScroll: true });
    }
    updateStoredPickerState();
    renderGiftGrid();
  }

  function resetCatalogFilters() {
    searchQuery = '';
    activeCategory = 'all';
    filterWithinWallet = false;
    sortMode = 'recommended';
    resetCatalogRenderLimit();
    var search = root.querySelector('[data-avora-gift-search]');
    if (search) search.value = '';
    updateStoredPickerState();
    renderGiftGrid();
    if (search) search.focus({ preventScroll: true });
  }

  function toggleSortMenu(trigger) {
    var menu = root.querySelector('[data-avora-sort-menu]');
    if (!menu) return;
    if (!menu.hidden && menu.classList.contains('is-open')) {
      closeSortMenu(true);
      return;
    }
    window.clearTimeout(sortCloseTimer);
    menu.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    menu.classList.add('is-open');
  }

  function closeSortMenu(restoreFocus) {
    var trigger = root && root.querySelector('[data-avora-sort-trigger]');
    var menu = root && root.querySelector('[data-avora-sort-menu]');
    if (!menu || menu.hidden) return;
    window.clearTimeout(sortCloseTimer);
    menu.classList.remove('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    sortCloseTimer = window.setTimeout(function () { menu.hidden = true; }, motionReduced() ? 0 : 170);
    if (restoreFocus && trigger) trigger.focus({ preventScroll: true });
  }

  function chooseSortMode(option) {
    sortMode = option.dataset.avoraSortOption || 'recommended';
    resetCatalogRenderLimit();
    updateStoredPickerState();
    closeSortMenu(true);
    renderGiftGrid();
    var grid = root.querySelector('[data-avora-gift-grid]');
    if (grid) grid.scrollTop = 0;
  }

  function handleSortKeydown(event) {
    var trigger = event.target.closest && event.target.closest('[data-avora-sort-trigger]');
    var option = event.target.closest && event.target.closest('[data-avora-sort-option]');
    var menu = root && root.querySelector('[data-avora-sort-menu]');
    if (menu && !menu.hidden && event.key === 'Escape') {
      event.preventDefault();
      closeSortMenu(true);
      return true;
    }
    if (trigger && (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      if (menu && (menu.hidden || !menu.classList.contains('is-open'))) toggleSortMenu(trigger);
      var selected = menu && menu.querySelector('[aria-selected="true"]');
      if (selected) selected.focus({ preventScroll: true });
      return true;
    }
    if (!option || !menu || menu.hidden) return false;
    var options = Array.prototype.slice.call(menu.querySelectorAll('[data-avora-sort-option]'));
    var index = options.indexOf(option);
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      var next = event.key === 'ArrowDown' ? (index + 1) % options.length : (index - 1 + options.length) % options.length;
      options[next].focus({ preventScroll: true });
      return true;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      closeSortMenu(true);
      return true;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      chooseSortMode(option);
      return true;
    }
    return false;
  }

  function toggleGift(button) {
    var removing = !!button.dataset.lineKey;
    setGiftQuantity(button.dataset.variantId, removing ? 0 : 1, button, !removing);
  }

  function handleProductVariant(event) {
    var panel = document.querySelector('[data-avora-product-gift]');
    var detail = event && event.detail;
    if (!panel || !detail || !detail.variantId) return;
    panel.dataset.variantId = String(detail.variantId);
    panel.dataset.variantPrice = String(Number(detail.price) || 0);
    panel.dataset.variantAvailable = String(!!detail.available);
    renderProductGift();
  }

  function renderProductGift() {
    var panel = document.querySelector('[data-avora-product-gift]');
    if (!panel || !state || typeof state.wallet !== 'number') return;
    var variantId = String(panel.dataset.variantId || '');
    var variantPrice = Number(panel.dataset.variantPrice) || 0;
    var available = panel.dataset.variantAvailable !== 'false';
    var selection = giftSelectionsByVariant()[variantId];
    var selectedQuantity = selection ? selection.quantity : 0;
    var remaining = confirmedWalletRemaining();
    var eligible = panel.dataset.productEligible !== 'false' && variantPrice >= 1;
    panel.hidden = !core.shouldShowProductGift(state, selectedQuantity, eligible, available);
    if (panel.hidden) return;

    var incrementalTopup = Math.max(0, state.giftTotal + variantPrice - state.wallet) - state.topup;
    var label = panel.querySelector('[data-avora-product-gift-label]');
    var message = panel.querySelector('[data-avora-product-gift-message]');
    var progress = panel.querySelector('[data-avora-product-gift-progress]');
    var button = panel.querySelector('[data-avora-pdp-gift-action]');
    var quantityControl = panel.querySelector('[data-avora-pdp-gift-quantity]');
    var quantityValue = panel.querySelector('[data-avora-pdp-gift-quantity-value]');

    if (label) {
      label.textContent = selectedQuantity
        ? selectedQuantity + (selectedQuantity === 1 ? window.AvoraI18n.text(' gift selected') : window.AvoraI18n.text(' gifts selected'))
        : money(state.wallet) + window.AvoraI18n.text(' Gift Wallet');
    }
    if (message) {
      if (selectedQuantity && remaining < 1) message.textContent = window.AvoraI18n.text('Your Gift Wallet is fully applied. You can reduce or remove this gift.');
      else if (!available) message.textContent = selectedQuantity
        ? window.AvoraI18n.text('This option is now sold out. You can reduce or remove it from your gifts.')
        : window.AvoraI18n.text('This option is sold out. Choose another option to add it as a gift.');
      else if (incrementalTopup > 0) message.textContent = window.AvoraI18n.text('Avora covers your wallet balance. Add this with a ') + money(incrementalTopup) + ' top-up.';
      else if (selectedQuantity) message.textContent = money(remaining) + window.AvoraI18n.text(' remains in your Gift Wallet.');
      else message.textContent = window.AvoraI18n.text('This option is covered by your Gift Wallet.');
    }
    if (progress) progress.style.transform = 'scaleX(' + (state.wallet ? Math.min(1, state.giftTotal / state.wallet) : 0) + ')';
    if (button) {
      button.dataset.variantId = variantId;
      button.hidden = !!selectedQuantity;
      button.disabled = giftMutationPending || !eligible || !available || remaining < 1;
      button.removeAttribute('aria-busy');
      button.classList.remove('is-adding');
      button.textContent = available ? window.AvoraI18n.text('Add this as a gift') : window.AvoraI18n.text('Option sold out');
      button.setAttribute('aria-label', available
        ? 'Add ' + panel.dataset.productTitle + window.AvoraI18n.text(' to your gifts')
        : panel.dataset.productTitle + window.AvoraI18n.text(' is sold out'));
    }
    if (quantityControl) quantityControl.hidden = !selectedQuantity;
    if (quantityValue) quantityValue.textContent = String(selectedQuantity || 0);
    if (quantityControl) {
      quantityControl.querySelectorAll('[data-avora-gift-quantity]').forEach(function (control) {
        var delta = Number(control.dataset.delta) || 0;
        control.dataset.variantId = variantId;
        control.dataset.quantity = String(selectedQuantity || 0);
        control.disabled = giftMutationPending || !selectedQuantity || (delta > 0 && (!available || remaining < 1));
      });
    }
    panel.classList.toggle('has-selection', !!selectedQuantity);
  }

  function addProductPageGift(button) {
    if (confirmedWalletRemaining() < 1 || button.disabled) return;
    var variantId = String(button.dataset.variantId || '');
    var selected = giftSelectionsByVariant()[variantId];
    setGiftQuantity(variantId, (selected ? selected.quantity : 0) + 1, button, true);
  }

  function updateGiftQuantity(button) {
    if (button.disabled) return;
    var variantId = String(button.dataset.variantId || '');
    var selected = giftSelectionsByVariant()[variantId];
    if (!selected) return;
    // Cart rows have a separate Remove action; minus never deletes the last gift.
    if (button.closest('.cart-page, #avora-cart-drawer') && Number(button.dataset.delta) < 0 && selected.quantity <= 1) return;
    var quantity = Math.max(0, selected.quantity + Number(button.dataset.delta || 0));
    setGiftQuantity(variantId, quantity, button, false);
  }

  function collectRewardCartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (component) {
      if (component.dataset.sectionId && ids.indexOf(component.dataset.sectionId) === -1) {
        ids.push(component.dataset.sectionId);
      }
    });
    return ids;
  }

  function rewardParentLine(currentCart, giftVariantId) {
    return (currentCart.items || []).filter(function (item) {
      return core.isEligiblePaidItem(item, paidEligibility)
        && String(item.variant_id) !== String(giftVariantId);
    })[0] || null;
  }

  function addNestedRewardGift(variantId, quantity, sectionIds, currentCart) {
    var parent = rewardParentLine(currentCart, variantId);
    if (!parent) return Promise.reject({ description: window.AvoraI18n.text('This exact item is already the only paid product in your cart. Add a different paid product before selecting it as a gift.') });

    var addPayload = {
      items: [{
        id: Number(variantId),
        quantity: quantity,
        parent_line_key: parent.key,
        properties: {
          '_avora_reward_gift': 'true',
          '_avora_reward_version': '4',
          '_avora_reward_tier': state.currentTier ? String(state.currentTier.threshold) : '',
          '_avora_reward_wallet': String(state.wallet || 0),
          '_avora_reward_card': String(state.futureCard || 0),
          '_avora_reward_card_minimum': String(state.futureMinimum || 0)
        }
      }],
      sections_url: window.location.pathname
    };
    if (sectionIds.length) addPayload.sections = sectionIds.join(',');

    return fetchJSON(normalisedRootUrl() + 'cart/add.js', {
      method: 'POST', timeout: 12000,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(addPayload)
    }).then(function (addResponse) {
      return fetchJSON(normalisedRootUrl() + 'cart.js', { timeout: 10000 }).then(function (nextCart) {
        return { cart: nextCart, sections: addResponse.sections || {} };
      });
    });
  }

  function setGiftQuantity(variantId, quantity, button, celebrate) {
    var chargeConfirmed = Number(arguments[4]) || 0;
    var selected = giftSelectionsByVariant()[String(variantId)];
    var selectedQuantity = selected ? selected.quantity : 0;
    var card = button && button.closest('[data-avora-gift-card]');
    var cardHandle = card ? card.dataset.productHandle : '';
    var sectionIds = collectRewardCartSectionIds();
    if (!variantId || quantity < 0 || giftMutationPending) return;
    if (quantity > selectedQuantity && confirmedWalletRemaining() < 1) {
      renderGiftGrid();
      renderProductGift();
      syncGiftQuantityControls();
      if (window.avoraCartDrawer && window.avoraCartDrawer.toast) {
        window.avoraCartDrawer.toast(window.AvoraI18n.text('Your Gift Wallet is fully applied. Reduce a gift before adding another.'));
      }
      return;
    }
    if (!selected && quantity === 0) return;
    if (quantity === 0 && !arguments[5]) {
      if (!window.AvoraCartInteractions) return;
      var removingItem = selected.items && selected.items[0];
      if (!removingItem) return;
      window.AvoraCartInteractions.confirmRemoval(removingItem.key,removingItem.product_title || removingItem.title,button).then(function(approved) {
        if (approved) setGiftQuantity(variantId,quantity,button,celebrate,chargeConfirmed,true);
      });
      return;
    }
    if (quantity > selectedQuantity && window.AvoraCartInteractions) {
      var selectedItem = selected && selected.items && selected.items[0];
      var chargeVariant = null;
      catalog.some(function(product) { chargeVariant = findVariant(product, variantId); return !!chargeVariant; });
      var unitValue = selectedItem ? Number(selectedItem.original_price || selectedItem.price) : chargeVariant && Number(chargeVariant.price);
      var addedCharge = Math.max(0, state.giftTotal + (quantity - selectedQuantity) * (unitValue || 0) - state.wallet) - state.topup;
      if (addedCharge > chargeConfirmed) {
        window.AvoraCartInteractions.confirmCharge(money(addedCharge) + window.AvoraI18n.text(' will be added to your total beyond your Gift Wallet.')).then(function(approved) {
          if (approved) setGiftQuantity(variantId, quantity, button, celebrate, addedCharge);
        });
        return;
      }
    }
    giftMutationPending = true;
    syncGiftQuantityControls();
    if (card) card.setAttribute('aria-busy', 'true');
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (celebrate) {
        button.classList.add('is-adding');
        button.innerHTML = window.AvoraI18n.fragment('<span class="avora-rewards__gift-action-spinner" aria-hidden="true"></span><span>Adding your gift…</span>');
      }
    }

    enqueueMutation(function () {
      return fetchJSON(normalisedRootUrl() + 'cart.js', { timeout: 10000 }).then(function (freshCart) {
        return hydratePaidEligibility(freshCart, quantity > selectedQuantity).then(function() { return freshCart; });
      }).then(function (freshCart) {
        update(freshCart);
        var freshSelection = giftSelectionsByVariant()[String(variantId)];
        var freshQuantity = freshSelection ? freshSelection.quantity : 0;
        if (!core.canChangeGift(state, freshQuantity, quantity)) {
          throw { description: window.AvoraI18n.text('Your Gift Wallet is fully used. Remove a gift before adding another.') };
        }
        var freshCharge = Math.max(0, state.giftTotal + Math.max(0, quantity - freshQuantity) * (unitValue || 0) - state.wallet) - state.topup;
        if (quantity > freshQuantity && freshCharge > chargeConfirmed && window.AvoraCartInteractions) {
          throw { description: window.AvoraI18n.text('Your cart changed. Review the updated gift amount and try again.') };
        }
        if (freshSelection && freshSelection.items.length) {
          var updates = {};
          freshSelection.items.forEach(function (item, index) {
            var nested = item.parent_relationship && item.parent_relationship.parent_key;
            updates[item.key] = nested && index === 0 ? quantity : 0;
          });
          var updatePayload = { updates: updates, sections_url: window.location.pathname };
          if (sectionIds.length) updatePayload.sections = sectionIds.join(',');
          return fetchJSON(normalisedRootUrl() + 'cart/update.js', {
            method: 'POST', timeout: 12000,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updatePayload)
          }).then(function (nextCart) {
            var hasStandaloneGift = freshSelection.items.some(function (item) {
              return !(item.parent_relationship && item.parent_relationship.parent_key);
            });
            if (quantity > 0 && hasStandaloneGift) return addNestedRewardGift(variantId, quantity, sectionIds, nextCart);
            return { cart: nextCart, sections: nextCart.sections || {} };
          });
        }
        if (quantity > 0) return addNestedRewardGift(variantId, quantity, sectionIds, freshCart);
        return { cart: freshCart, sections: {} };
      });
    }).then(function (result) {
      var nextCart = result.cart;
      clearRewardErrors();
      giftMutationPending = false;
      if (quantity === 0) selectRemainingProductVariant(variantId, nextCart);
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          cart: nextCart,
          resource: nextCart,
          sourceId: 'avora-rewards',
          data: {
            source: 'avora-rewards',
            cart: nextCart,
            itemCount: Number(nextCart.item_count) || 0,
            sections: result.sections
          }
        }
      }));
      if (window.avoraCartDrawer && typeof window.avoraCartDrawer.render === 'function') window.avoraCartDrawer.render(nextCart);
      if (celebrate) window.setTimeout(function () { celebrateGiftSelection(String(variantId)); }, 30);
      window.setTimeout(function () { focusGiftControl(cardHandle); }, 80);
    }).catch(function (error) {
      giftMutationPending = false;
      if (card) card.removeAttribute('aria-busy');
      fetchCart().then(function () { renderGiftGrid(); renderProductGift(); });
      showRewardError((error && error.description) || window.AvoraI18n.text('We could not update that gift. Please try again.'));
    });
  }

  function syncGiftQuantityControls() {
    var selections = state && state.giftItems ? giftSelectionsByVariant() : {};
    var remaining = confirmedWalletRemaining();
    document.querySelectorAll('[data-avora-gift-quantity]').forEach(function (control) {
      var variantId = String(control.dataset.variantId || '');
      var selection = selections[variantId];
      var delta = Number(control.dataset.delta) || 0;
      control.dataset.quantity = String(selection ? selection.quantity : 0);
      var cartMinimum = control.closest('.cart-page, #avora-cart-drawer') && delta < 0 && selection && selection.quantity <= 1;
      control.disabled = giftMutationPending || !selection || cartMinimum || (delta > 0 && remaining < 1);
      control.setAttribute('aria-disabled', String(control.disabled));
    });
    if (giftMutationPending && root) {
      root.querySelectorAll('[data-avora-gift-action]').forEach(function (control) { control.disabled = true; });
    }
    updatePickerTotals();
  }

  function focusGiftControl(cardHandle) {
    if (!cardHandle || !activeDialog || !activeDialog.matches('[data-avora-gift-picker]')) return;
    var card = activeDialog.querySelector('[data-product-handle="' + escapeSelector(cardHandle) + '"]');
    var control = card && (card.querySelector('[data-avora-focus-key="increase"]')
      || card.querySelector('[data-avora-focus-key="add"]')
      || card.querySelector('[data-avora-focus-key="variant"]'));
    if (control) control.focus({ preventScroll: true });
  }

  function selectRemainingProductVariant(removedVariantId, nextCart) {
    var product = catalog.filter(function (candidate) {
      return !!findVariant(candidate, removedVariantId);
    })[0];
    if (!product || !nextCart || !Array.isArray(nextCart.items)) return;
    for (var i = 0; i < nextCart.items.length; i += 1) {
      var item = nextCart.items[i];
      if (isRewardGift(item) && findVariant(product, item.variant_id)) {
        var remainingVariant = findVariant(product, item.variant_id);
        variantSelections[product.handle] = String(remainingVariant.id);
        selectProductVariantImage(product, remainingVariant);
        updateStoredPickerState();
        return;
      }
    }
  }

  function readCartResponse(response) {
    if (!response.ok) return response.json().then(function (error) { throw error; });
    return response.json();
  }

  function updateCheckout() {
    var checkouts = checkoutElements();
    var subtotal = document.getElementById('avora-drawer-subtotal');
    if (subtotal) subtotal.textContent = money(state.estimatedTotal);
    var cartRewards = document.querySelector('[data-avora-cart-rewards]');
    var cartPage = cartRewards && cartRewards.closest('.cart-page');
    var cartPageTotal = cartPage && cartPage.querySelector('[data-testid="cart-total-value"]');
    var cartWalletAdjustment = cartPage && cartPage.querySelector('[data-avora-cart-wallet-adjustment]');
    if (cartPageTotal) {
      var estimatedTotal = money(state.estimatedTotal);
      cartPageTotal.textContent = estimatedTotal;
      cartPageTotal.setAttribute('value', estimatedTotal);
      var cartCaptionTotal = cartPage.querySelector('.cart-items__table caption span');
      if (cartCaptionTotal) cartCaptionTotal.textContent = estimatedTotal;
    }
    if (cartWalletAdjustment) {
      cartWalletAdjustment.hidden = !state.pendingWalletAdjustment;
      setText(cartWalletAdjustment, '[data-avora-cart-wallet-adjustment-value]', '−' + money(state.pendingWalletAdjustment));
    }
    checkouts.forEach(function (checkout) {
      var managedLink = checkout.hasAttribute('data-avora-checkout');
      var nativeSubmit = checkout.matches('button, input');
      if (managedLink) checkout.href = normalisedRootUrl() + 'cart#' + (core.requiresGiftDecision(state) ? 'avora-gift-wallet' : 'avora-checkout');
      checkout.setAttribute('aria-busy', checkoutPreparing ? 'true' : 'false');
      if (nativeSubmit) {
        if (!checkout.hasAttribute('data-avora-idle-disabled')) {
          checkout.setAttribute('data-avora-idle-disabled', checkout.disabled ? 'true' : 'false');
        }
        checkout.disabled = checkoutPreparing || checkout.getAttribute('data-avora-idle-disabled') === 'true';
      }
      var label = checkout.querySelector('[data-avora-checkout-label], .button-text');
      if (label) {
        if (!managedLink && !label.hasAttribute('data-avora-idle-label')) {
          label.setAttribute('data-avora-idle-label', label.textContent.trim());
        }
        if (checkoutPreparing) {
          label.textContent = window.AvoraI18n.text('Preparing your rewards…');
        } else if (core.requiresGiftDecision(state)) {
          label.textContent = window.AvoraI18n.text('Review Gift Wallet');
        } else if (managedLink) {
          label.textContent = state.walletReady ? window.AvoraI18n.text('Rewards secured · Checkout') : window.AvoraI18n.text('Checkout');
        } else {
          label.textContent = label.getAttribute('data-avora-idle-label') || window.AvoraI18n.text('Checkout securely');
        }
      }
    });
  }

  function setCheckoutPreparing(preparing) {
    checkoutPreparing = preparing;
    updateCheckout();
  }

  function checkoutGiftSelectionFingerprint(currentState) {
    return JSON.stringify((currentState.giftItems || []).map(function (item) {
      return [String(item.variant_id), Number(item.quantity) || 0];
    }).sort(function (left, right) {
      return left[0].localeCompare(right[0]) || left[1] - right[1];
    }));
  }

  var approvedTopup = null;

  function beginCheckout(skipWallet) {
    if (checkoutPreparing) return;
    clearRewardErrors();
    setCheckoutPreparing(true);
    var target = core.checkoutUrl();
    var expectedWalletCredit = 0;
    var expectedGiftSelections = '';
    enqueueMutation(function () {
      return waitForActiveCartMutations().then(function() {
        return fetchJSON(normalisedRootUrl() + 'cart.js', { timeout: 10000 });
      }).then(function (freshCart) {
        cart = freshCart;
        return Promise.all([
          hydratePaidEligibility(freshCart, true),
          hydrateGiftVariantValidation(freshCart, true)
        ]).then(function () {
          state = calculateState(freshCart);
          return freshCart;
        });
      }).then(function (freshCart) {
        var unsecuredGifts = (state.invalidGiftItems || []).slice();
        (state.giftItems || []).forEach(function(item) {
          if (giftVariantValidation[String(item.variant_id)] !== true && unsecuredGifts.indexOf(item) === -1) {
            unsecuredGifts.push(item);
          }
        });
        if (!unsecuredGifts.length) return freshCart;
        state.invalidGiftItems = unsecuredGifts;
        setCheckoutPreparing(false);
        openGuard(Math.max(0, state.wallet - state.giftTotal));
        return null;
      }).then(function (securedCart) {
        if (!securedCart) return null;
        cart = securedCart;
        state = calculateState(securedCart);
        var topupKey = String(state.topup) + ':' + checkoutGiftSelectionFingerprint(state);
        if (state.topup > 0 && window.AvoraCartInteractions && approvedTopup !== topupKey) {
          setCheckoutPreparing(false);
          window.AvoraCartInteractions.confirmCharge(money(state.topup) + window.AvoraI18n.text(' of your selected gifts is payable beyond your wallet. This amount is included in your estimated total.'), window.AvoraI18n.text('Accept & continue')).then(function(approved) {
            if (approved) { approvedTopup = topupKey; beginCheckout(skipWallet); }
          });
          return null;
        }

      if (!skipWallet && core.requiresGiftDecision(state)) {
        setCheckoutPreparing(false);
        openGuard(Math.max(0, state.wallet - state.giftTotal));
        if (state.invalidGiftItems.length && window.avoraCartDrawer && window.avoraCartDrawer.toast) {
          window.avoraCartDrawer.toast(window.AvoraI18n.text('For security, please reselect your Gift Wallet items.'));
        }
        return null;
      }
      expectedWalletCredit = skipWallet ? 0 : state.appliedCredit;
      expectedGiftSelections = checkoutGiftSelectionFingerprint(state);
      return fetchJSON(normalisedRootUrl() + 'cart/update.js', {
        method: 'POST', timeout: 12000,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: core.rewardAttributes(state, config.version, config.currencyRate),
          discount: core.checkoutDiscountCodes(cart, state, config.codes, !!skipWallet).join(',')
        })
      });
      });
    }).then(function (nextCart) {
      if (!nextCart) return;
      cart = nextCart;
      state = calculateState(nextCart);
      // HTTP 200 only acknowledges the cart write: Shopify can reject a discount
      // with a successful response. Require actual Gift Wallet allocations before
      // taking the buyer to checkout; unrelated promotions are not wallet proof.
      if ((state.invalidGiftItems || []).length
        || checkoutGiftSelectionFingerprint(state) !== expectedGiftSelections
        || (skipWallet && state.giftItems.length)
        || (!skipWallet && (state.walletDiscountTotal < expectedWalletCredit
          || state.walletDiscountTotal < state.appliedCredit))) {
        throw { rewardMessage: window.AvoraI18n.text('Your Gift Wallet discount could not be confirmed. Your gifts have been kept. Please try checkout again.') };
      }
      window.location.assign(target);
    }).catch(function (error) {
      setCheckoutPreparing(false);
      showRewardError((error && error.rewardMessage) || window.AvoraI18n.text('We could not verify your rewards. Your gift selections have been kept. Please try checkout again.'));
    });
  }

  function dialogLauncherSelector(element) {
    if (!element || element.nodeType !== 1) return '';
    if (element.matches('[data-avora-cart-reward-gifts]')) return '[data-avora-cart-reward-gifts]';
    if (element.matches('[data-avora-cart-reward-map]')) return '[data-avora-cart-reward-map]';
    if (element.matches('[data-avora-pdp-gift-action]')) return '[data-avora-pdp-gift-action]';
    if (element.matches('.avora-product-gift__browse')) return '.avora-product-gift__browse';
    if (element.closest('#avora-cart-drawer')) {
      if (element.matches('[data-avora-open-gifts]')) return '#avora-cart-drawer [data-avora-open-gifts]';
      if (element.matches('[data-avora-open-rewards]')) return '#avora-cart-drawer [data-avora-open-rewards]';
      if (element.matches('[data-avora-checkout]')) return '#avora-drawer-checkout';
    }
    if (element.matches('[data-avora-checkout]') && element.id) return '#' + element.id;
    if (element.matches('button[name="checkout"]')) return 'button[name="checkout"]';
    if (element.matches('input[name="checkout"]')) return 'input[name="checkout"]';
    if (element.matches('[data-avora-open-gifts]')) return '[data-avora-open-gifts]';
    if (element.matches('[data-avora-reward-dock-shell] [data-avora-open-rewards]')) {
      return '[data-avora-reward-dock-shell] [data-avora-open-rewards]';
    }
    if (element.matches('[data-avora-open-rewards]')) return '[data-avora-open-rewards]';
    return '';
  }

  function rememberDialogLauncher() {
    lastFocus = document.activeElement;
    lastFocusSelector = dialogLauncherSelector(lastFocus);
  }

  function focusDialogLauncher() {
    var target = lastFocus && document.documentElement.contains(lastFocus) ? lastFocus : null;
    if (!target && lastFocusSelector) target = document.querySelector(lastFocusSelector);
    if (!target || target.disabled || target.closest('[hidden]') || typeof target.focus !== 'function') return;
    try { target.focus({ preventScroll: true }); } catch (error) { target.focus(); }
    lastFocus = target;
  }

  function restoreDialogLauncherFocus() {
    focusDialogLauncher();
    // Horizon can morph the cart section after a reward cart update. If that
    // replaces the launcher after the synchronous focus call, restore focus to
    // its freshly rendered equivalent without interrupting a user who has
    // already moved elsewhere.
    window.requestAnimationFrame(function () {
      if (document.activeElement === document.body || !document.activeElement) focusDialogLauncher();
    });
  }

  function openDialog(type) {
    if (!syncRoot()) return;
    var dialog = root.querySelector(type === 'picker' ? '[data-avora-gift-picker]' : '[data-avora-rewards-map]');
    if (!dialog) return;
    var drawer = document.getElementById('avora-cart-drawer');
    var drawerWasOpen = !!(drawer && drawer.classList.contains('is-open'));
    // Preserve the original launcher while moving between the reward map,
    // checkout guard and picker. Focusing a control in a closing dialog would
    // otherwise strand keyboard users on a hidden element.
    if (!activeDialog) rememberDialogLauncher();
    closeDialogs(false);
    if (drawerWasOpen && window.avoraCartDrawer && typeof window.avoraCartDrawer.close === 'function') {
      window.avoraCartDrawer.close();
      reopenCartOnClose = true;
    }
    root.querySelector('[data-avora-rewards-scrim]').hidden = false;
    dialog.hidden = false;
    activeDialog = dialog;
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('avora-rewards-open');
    if (type === 'picker') {
      var search = dialog.querySelector('[data-avora-gift-search]');
      if (search) search.value = searchQuery;
      var filter = dialog.querySelector('[data-avora-picker-filter]');
      if (filter) {
        filter.setAttribute('aria-pressed', String(filterWithinWallet));
        var filterLabel = filter.querySelector('[data-avora-wallet-filter-label]');
        if (filterLabel) filterLabel.textContent = filterWithinWallet ? window.AvoraI18n.text('Wallet-fit only') : window.AvoraI18n.text('Fits my wallet');
      }
      if (!catalogRenderLimit) resetCatalogRenderLimit();
      window.requestAnimationFrame(function () {
        if (activeDialog === dialog) renderGiftGrid();
      });
      loadCompleteCatalog();
    }
    if (type === 'map') animateJourneyMap(dialog);
    var heading = dialog.querySelector('h2');
    if (heading) window.setTimeout(function () { heading.focus({ preventScroll: true }); }, 80);
  }

  function openGuard(unused) {
    var drawer = document.getElementById('avora-cart-drawer');
    var drawerWasOpen = !!(drawer && drawer.classList.contains('is-open'));
    if (!activeDialog) rememberDialogLauncher();
    closeDialogs(false);
    if (drawerWasOpen && window.avoraCartDrawer && typeof window.avoraCartDrawer.close === 'function') {
      window.avoraCartDrawer.close();
      reopenCartOnClose = true;
    }
    var guard = root.querySelector('[data-avora-checkout-guard]');
    var scrim = root.querySelector('[data-avora-rewards-scrim]');
    if (!guard || !scrim) return;
    var choose = guard.querySelector('[data-avora-guard-choose]');
    var walletPaused = !state.wallet && state.giftTotal;
    var walletInvalid = !!(state.invalidGiftItems && state.invalidGiftItems.length);
    setGuardContinueBusy(false);
    setText(guard, '[data-avora-guard-wallet]', walletPaused ? 'paused' : money(state.wallet));
    setText(guard, '[data-avora-guard-copy]', walletInvalid
      ? window.AvoraI18n.text('We could not verify one or more gift selections. Open the Gift Wallet to reselect them, or continue and we’ll remove them before checkout.')
      : (walletPaused
      ? window.AvoraI18n.text('Your paid-product subtotal fell below ') + money(firstTierThreshold()) + '. Add ' + money(Math.max(0, firstTierThreshold() - state.paidSubtotal)) + window.AvoraI18n.text(' in products to restore your wallet, or continue and we’ll remove the gift selections.')
      : window.AvoraI18n.text('Your Gift Wallet is optional. Open it to adjust your selections, or continue without the current selections.')));
    setText(guard, '[data-avora-guard-selected]', walletInvalid
      ? state.invalidGiftItems.length + (state.invalidGiftItems.length === 1 ? window.AvoraI18n.text(' selection needs attention') : window.AvoraI18n.text(' selections need attention'))
      : (walletPaused ? money(state.giftTotal) + window.AvoraI18n.text(' in paused gift selections') : money(state.giftTotal) + ' selected'));
    setText(guard, '[data-avora-guard-remaining]', walletInvalid
      ? window.AvoraI18n.text('Reselect gifts')
      : (walletPaused ? money(Math.max(0, firstTierThreshold() - state.paidSubtotal)) + window.AvoraI18n.text(' to restore') : money(Math.max(0, state.wallet - state.giftTotal)) + ' available'));
    if (choose) {
      choose.textContent = walletInvalid ? window.AvoraI18n.text('Reselect my gifts') : (walletPaused ? window.AvoraI18n.text('Open paused Gift Wallet') : window.AvoraI18n.text('Choose my gifts'));
    }
    var guardProgress = guard.querySelector('[data-avora-guard-progress]');
    var guardProgressValue = walletInvalid
      ? 0
      : (walletPaused
      ? Math.min(1, state.paidSubtotal / firstTierThreshold())
      : (state.wallet ? Math.min(1, state.giftTotal / state.wallet) : 0));
    if (guardProgress) {
      guardProgress.style.setProperty('--guard-progress', guardProgressValue);
      guardProgress.style.transform = 'scaleX(' + guardProgressValue + ')';
    }
    var guardTrack = guard.querySelector('.avora-rewards__guard-track');
    if (guardTrack) {
      guardTrack.setAttribute('aria-valuenow', String(Math.round(guardProgressValue * 100)));
      guardTrack.setAttribute('aria-valuetext', walletInvalid
        ? window.AvoraI18n.text('Gift selections need to be reselected')
        : (walletPaused
        ? money(Math.max(0, firstTierThreshold() - state.paidSubtotal)) + window.AvoraI18n.text(' in paid products needed to restore the Gift Wallet')
        : money(Math.max(0, state.wallet - state.giftTotal)) + window.AvoraI18n.text(' remains available in the Gift Wallet')));
    }
    scrim.hidden = false;
    guard.hidden = false;
    activeDialog = guard;
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('avora-rewards-open');
    replayMotion(guard, 'is-revealing', 1200);
    window.setTimeout(function () { guard.querySelector('[data-avora-guard-choose]').focus(); }, 80);
  }

  function guardPrimaryAction() {
    openDialog('picker');
  }

  function closeDialogs(restoreFocus) {
    var shouldReopenCart = reopenCartOnClose && restoreFocus !== false;
    closeVariantDropdown(false);
    closeSortMenu(false);
    root.querySelectorAll('[data-avora-rewards-map], [data-avora-gift-picker], [data-avora-checkout-guard]').forEach(function (node) { node.hidden = true; });
    var scrim = root.querySelector('[data-avora-rewards-scrim]');
    if (scrim) scrim.hidden = true;
    activeDialog = null;
    document.body.style.overflow = previousBodyOverflow;
    document.body.classList.remove('avora-rewards-open');
    if (shouldReopenCart && window.avoraCartDrawer && typeof window.avoraCartDrawer.open === 'function') {
      reopenCartOnClose = false;
      window.avoraCartDrawer.open(cart);
    } else if (restoreFocus !== false) {
      restoreDialogLauncherFocus();
    }
  }

  function mapPrimaryAction() {
    if (state.wallet) openDialog('picker');
    else {
      closeDialogs(false);
      reopenCartOnClose = false;
      window.location.href = normalisedRootUrl() + 'collections/all';
    }
  }

  function finishPicking() {
    if (giftMutationPending) return;
    markPickerJourneyComplete();
    // The explicit cart action reviews the saved gifts. Close/Escape still
    // return to the original launcher; a full cart page never opens a drawer.
    if (/\/cart\/?$/.test(window.location.pathname)) reopenCartOnClose = false;
    else {
      if (window.avoraCartDrawer && typeof window.avoraCartDrawer.open === 'function') reopenCartOnClose = true;
      else {
        closeDialogs(false);
        window.location.href = normalisedRootUrl() + 'cart';
        return;
      }
    }
    closeDialogs(true);
  }

  function rememberPickerJourney(link) {
    var grid = root.querySelector('[data-avora-gift-grid]');
    var card = link.closest('[data-avora-gift-card]');
    var originPath = currentPath();
    writePickerReturn({
      active: true,
      originPath: originPath,
      returnUrl: originPath + '#avora-gift-picker',
      destination: link.href,
      gridScroll: grid ? grid.scrollTop : 0,
      focusHandle: card ? card.dataset.productHandle : '',
      imageIndexes: imageIndexes,
      variantSelections: variantSelections,
      filterWithinWallet: filterWithinWallet,
      searchQuery: searchQuery,
      activeCategory: activeCategory,
      sortMode: sortMode,
      savedAt: Date.now()
    });
  }

  function restorePickerJourney() {
    var saved = readPickerReturn();
    if (!saved || !saved.active) return;
    if (!state || typeof state.paidCount !== 'number') return;
    if (!state.paidCount) {
      clearPickerReturn();
      return;
    }
    if (saved.originPath !== currentPath()) {
      renderPickerReturn();
      renderDock();
      return;
    }

    imageIndexes = saved.imageIndexes || imageIndexes;
    variantSelections = saved.variantSelections || variantSelections;
    filterWithinWallet = !!saved.filterWithinWallet;
    searchQuery = typeof saved.searchQuery === 'string' ? saved.searchQuery : searchQuery;
    activeCategory = saved.activeCategory || activeCategory;
    sortMode = saved.sortMode || sortMode;
    renderGiftGrid();
    if (state.wallet || state.giftTotal) openDialog('picker');
    else openDialog('map');
    saved.active = false;
    saved.savedAt = Date.now();
    writePickerReturn(saved);

    window.setTimeout(function () {
      var grid = root.querySelector('[data-avora-gift-grid]');
      if (grid) grid.scrollTop = Number(saved.gridScroll) || 0;
      if (saved.focusHandle && activeDialog && activeDialog.matches('[data-avora-gift-picker]')) {
        var card = activeDialog.querySelector('[data-product-handle="' + escapeSelector(saved.focusHandle) + '"]');
        var focusTarget = card && card.querySelector('[data-avora-product-link]');
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      }
    }, 160);

    if (window.location.hash === '#avora-gift-picker' && window.history && window.history.replaceState) {
      window.history.replaceState(window.history.state, '', currentPath());
    }
  }

  function renderPickerReturn() {
    var reminder = root && root.querySelector('[data-avora-picker-return]');
    if (!reminder) return;
    var saved = readPickerReturn();
    var show = !!(saved && saved.active && saved.originPath !== currentPath() && state.paidCount);
    var link = reminder.querySelector('[data-avora-return-gifts]');
    if (link && saved && saved.returnUrl) link.href = saved.returnUrl;
    reminder.hidden = !show;
  }

  function shouldShowPickerReturn() {
    var saved = readPickerReturn();
    return !!(saved && saved.active && saved.originPath !== currentPath() && state.paidCount);
  }

  function dismissPickerReturn() {
    clearPickerReturn();
    renderPickerReturn();
    renderDock();
  }

  function markPickerJourneyComplete() {
    var saved = readPickerReturn();
    if (!saved) return;
    saved.active = false;
    saved.imageIndexes = imageIndexes;
    saved.variantSelections = variantSelections;
    saved.filterWithinWallet = filterWithinWallet;
    saved.searchQuery = searchQuery;
    saved.activeCategory = activeCategory;
    saved.sortMode = sortMode;
    saved.savedAt = Date.now();
    writePickerReturn(saved);
  }

  function updateStoredPickerState() {
    var saved = readPickerReturn();
    if (!saved) return;
    saved.imageIndexes = imageIndexes;
    saved.variantSelections = variantSelections;
    saved.filterWithinWallet = filterWithinWallet;
    saved.searchQuery = searchQuery;
    saved.activeCategory = activeCategory;
    saved.sortMode = sortMode;
    saved.savedAt = Date.now();
    writePickerReturn(saved);
  }

  function readPickerReturn() {
    var raw = readSession(pickerReturnKey);
    if (!raw) return null;
    try {
      var saved = JSON.parse(raw);
      if (!saved.savedAt || Date.now() - saved.savedAt > pickerReturnMaxAge) {
        clearPickerReturn();
        return null;
      }
      return saved;
    } catch (error) {
      clearPickerReturn();
      return null;
    }
  }

  function writePickerReturn(value) {
    try { sessionStorage.setItem(pickerReturnKey, JSON.stringify(value)); } catch (error) {}
  }

  function clearPickerReturn() {
    try { sessionStorage.removeItem(pickerReturnKey); } catch (error) {}
  }

  function currentPath() {
    return window.location.pathname + window.location.search;
  }

  function setGuardContinueBusy(busy) {
    if (!root) return;
    var button = root.querySelector('[data-avora-guard-continue]');
    if (!button) return;
    button.disabled = !!busy;
    button.setAttribute('aria-busy', String(!!busy));
    button.textContent = busy ? window.AvoraI18n.text('Removing gift selections…') : window.AvoraI18n.text('Continue without Gift Wallet');
  }

  function continueCheckout() {
    closeDialogs(false);
    if (!state.giftItems.length) {
      beginCheckout(true);
      return;
    }

    var updates = {};
    state.giftItems.forEach(function (item) { updates[item.key] = 0; });
    setGuardContinueBusy(true);

    enqueueMutation(function () {
      return fetchJSON(normalisedRootUrl() + 'cart/update.js', {
        method: 'POST', timeout: 12000,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      });
    }).then(function (nextCart) {
      cart = nextCart;
      state = calculateState(nextCart);
      beginCheckout(true);
    }).catch(function () {
      setGuardContinueBusy(false);
      openGuard(Math.max(0, state.wallet - state.giftTotal));
      if (window.avoraCartDrawer && window.avoraCartDrawer.toast) window.avoraCartDrawer.toast(window.AvoraI18n.text('We could not remove your gift selections. Please try again.'));
    });
  }

  function celebrateGiftSelection(variantId) {
    var card = root.querySelector('[data-avora-gift-action][data-variant-id="' + escapeSelector(variantId) + '"]');
    card = card && card.closest('[data-avora-gift-card]');
    if (card) {
      var previousConfirmation = card.querySelector('[data-avora-gift-added]');
      if (previousConfirmation) previousConfirmation.remove();
      var remaining = Math.max(0, state.wallet - state.giftTotal);
      var detail = state.topup
        ? money(state.credit) + window.AvoraI18n.text(' covered by your Gift Wallet')
        : (remaining ? money(remaining) + window.AvoraI18n.text(' still yours to spend') : window.AvoraI18n.text('Your Gift Wallet is complete'));
      var confirmation = document.createElement('div');
      confirmation.className = 'avora-rewards__gift-added';
      confirmation.dataset.avoraGiftAdded = '';
      confirmation.setAttribute('role', 'status');
      confirmation.setAttribute('aria-live', 'polite');
      confirmation.innerHTML = '<span class="avora-rewards__gift-added-visual" aria-hidden="true">' +
          '<span class="avora-rewards__gift-added-check">' + config.checkmarkIcon + '</span>' +
          '<span class="avora-rewards__gift-added-sparks"><span></span><span></span><span></span><span></span><span></span><span></span><span></span><span></span></span>' +
        '</span>' +
        window.AvoraI18n.fragment('<strong>Gift added</strong>') +
        '<span>' + escapeHTML(detail) + '</span>';
      card.appendChild(confirmation);
      if (motionReduced()) confirmation.classList.add('is-reduced');
      else window.requestAnimationFrame(function () { confirmation.classList.add('is-active'); });
      window.setTimeout(function () {
        if (confirmation.parentNode) confirmation.parentNode.removeChild(confirmation);
      }, motionReduced() ? 1400 : 1750);
      replayMotion(card, 'is-just-selected', 1750);
    }
    celebrateProductGift(variantId);
    var meter = root.querySelector('.avora-rewards__picker-meter');
    if (meter) replayMotion(meter, 'is-updating', 1050);
  }

  function celebrateProductGift(variantId) {
    var panel = document.querySelector('[data-avora-product-gift]');
    if (!panel || panel.hidden || String(panel.dataset.variantId) !== String(variantId)) return;
    var confirmation = panel.querySelector('[data-avora-product-gift-confirmation]');
    var copy = panel.querySelector('[data-avora-product-gift-confirmation-copy]');
    if (!confirmation) return;
    var remaining = Math.max(0, state.wallet - state.giftTotal);
    if (copy) copy.textContent = state.topup
      ? money(state.credit) + window.AvoraI18n.text(' covered by your Gift Wallet')
      : (remaining ? money(remaining) + window.AvoraI18n.text(' is still yours to spend') : window.AvoraI18n.text('Your Gift Wallet is beautifully complete'));
    window.clearTimeout(confirmation._avoraHideTimer);
    confirmation.hidden = false;
    confirmation.classList.remove('is-active', 'is-reduced');
    void confirmation.offsetWidth;
    confirmation.classList.add(motionReduced() ? 'is-reduced' : 'is-active');
    replayMotion(panel, 'is-just-selected', 1900);
    confirmation._avoraHideTimer = window.setTimeout(function () {
      confirmation.classList.remove('is-active', 'is-reduced');
      confirmation.hidden = true;
    }, motionReduced() ? 1400 : 1900);
  }

  function tierCelebrationDialog() {
    var celebration = root.querySelector('[data-avora-celebration]');
    if (!celebration) return null;
    // Upgrade cached section markup as well as the current Liquid dialog. A
    // native modal owns the browser top layer; CSS z-index cannot compete with
    // another drawer's stacking context and does not make the background inert.
    if (celebration.tagName !== 'DIALOG') {
      var dialog = document.createElement('dialog');
      Array.prototype.forEach.call(celebration.attributes, function (attribute) {
        dialog.setAttribute(attribute.name, attribute.value);
      });
      while (celebration.firstChild) dialog.appendChild(celebration.firstChild);
      celebration.replaceWith(dialog);
      celebration = dialog;
    }
    celebration.removeAttribute('role');
    celebration.removeAttribute('aria-live');
    celebration.setAttribute('aria-modal', 'true');
    celebration.setAttribute('aria-labelledby', 'avora-celebration-title');
    celebration.setAttribute('aria-describedby', 'avora-celebration-detail');
    celebration.querySelector('[data-avora-celebration-title]').id = 'avora-celebration-title';
    celebration.querySelector('[data-avora-celebration-detail]').id = 'avora-celebration-detail';
    if (!celebration._avoraModalBound) {
      celebration._avoraModalBound = true;
      celebration.addEventListener('cancel', function (event) {
        event.preventDefault();
        hideTierCelebration(celebration);
      });
      celebration.addEventListener('keydown', function (event) {
        // The cart and rewards sheet retain their own keyboard handlers below.
        // Let the native dialog handle Tab/Escape without closing either one.
        if (event.key === 'Escape' || event.key === 'Tab') event.stopPropagation();
      });
      celebration.addEventListener('click', function (event) {
        if (event.target === celebration) hideTierCelebration(celebration);
      });
    }
    return celebration;
  }

  function celebrateNewTier(previousState) {
    if (!state.currentTier || !state.paidCount) return;
    if (!previousState || typeof previousState.paidSubtotal !== 'number') return;
    var previousThreshold = previousState.currentTier ? previousState.currentTier.threshold : 0;
    if (state.currentTier.threshold <= previousThreshold) return;
    var celebration = tierCelebrationDialog();
    if (!celebration) return;
    setText(celebration, '[data-avora-celebration-title]', state.currentTier.title);
    setText(celebration, '[data-avora-celebration-detail]', state.currentTier.detail);
    var chooseButton = celebration.querySelector('[data-avora-celebration-choose]');
    if (chooseButton) chooseButton.hidden = !state.wallet;
    window.clearTimeout(celebration._avoraHideTimer);
    window.cancelAnimationFrame(celebration._avoraStartFrame);
    celebration.classList.remove('is-active', 'is-reduced');
    celebration.hidden = false;
    if (!celebration.open) {
      celebration._avoraReturnFocus = document.activeElement;
      celebration._avoraPreviousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      celebration.showModal();
    }
    if (motionReduced()) {
      celebration.classList.add('is-reduced');
    } else {
      celebration._avoraStartFrame = window.requestAnimationFrame(function () {
        celebration.classList.add('is-active');
      });
    }
    // Interactive modal content stays available until dismissed or continued;
    // an automatic timeout would remove the focused action from keyboard users.
  }

  function hideTierCelebration(celebration) {
    celebration = celebration || root.querySelector('[data-avora-celebration]');
    if (!celebration) return;
    window.clearTimeout(celebration._avoraHideTimer);
    window.cancelAnimationFrame(celebration._avoraStartFrame);
    celebration.classList.remove('is-active', 'is-reduced');
    if (celebration.open) {
      celebration.close();
      document.body.style.overflow = celebration._avoraPreviousOverflow || '';
      var target = celebration._avoraReturnFocus;
      if (!target || !document.documentElement.contains(target) || target === document.body || target.closest('[hidden], [inert]')) {
        target = document.querySelector('#avora-cart-drawer.is-open #avora-drawer-close')
          || (activeDialog && activeDialog.querySelector('button:not([disabled])'));
      }
      if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
    }
    celebration.hidden = true;
  }

  function openCelebrationGiftPicker(button) {
    hideTierCelebration(button && button.closest('[data-avora-celebration]'));
    openDialog(state.wallet ? 'picker' : 'map');
  }

  function amountChanged(previousState, key) {
    return !!(previousState && typeof previousState[key] === 'number' && previousState[key] !== state[key]);
  }

  function amountIncreased(previousState, key) {
    return !!(previousState && typeof previousState[key] === 'number' && state[key] > previousState[key]);
  }

  function motionReduced() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function replayMotion(node, className, duration) {
    if (!node || motionReduced()) return;
    window.clearTimeout(node._avoraMotionTimer);
    node.classList.remove(className);
    void node.offsetWidth;
    node.classList.add(className);
    node._avoraMotionTimer = window.setTimeout(function () { node.classList.remove(className); }, duration || 900);
  }

  function animateMoney(node, from, to, duration) {
    if (!node) return;
    if (motionReduced() || !Number.isFinite(from) || !Number.isFinite(to) || from === to) {
      node.textContent = money(to);
      return;
    }
    window.cancelAnimationFrame(node._avoraCountFrame);
    var started = performance.now();
    function tick(now) {
      var progress = Math.min(1, (now - started) / duration);
      var eased = 1 - Math.pow(1 - progress, 3);
      node.textContent = money(Math.round(from + (to - from) * eased));
      if (progress < 1) node._avoraCountFrame = window.requestAnimationFrame(tick);
    }
    node._avoraCountFrame = window.requestAnimationFrame(tick);
  }

  function setText(scope, selector, value) {
    var node = scope && scope.querySelector(selector);
    if (node) node.textContent = value;
  }

  function money(cents) {
    var currency = config.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    if (window.AvoraMarketLocalization) {
      return window.AvoraMarketLocalization.formatMinor(cents, currency, config.locale || document.documentElement.lang || 'en');
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: currency, maximumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
    } catch (error) { return currency + ' ' + (cents / 100).toFixed(cents % 100 ? 2 : 0); }
  }

  function moneyShort(cents) { return money(cents); }
  function firstTierThreshold() { return config && config.tiers && config.tiers[0] ? config.tiers[0].threshold : 0; }
  function escapeHTML(value) { var div = document.createElement('div'); div.textContent = value || ''; return div.innerHTML; }
  function escapeAttr(value) { return String(value || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
  function escapeSelector(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(String(value || ''));
    return String(value || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  window.AvoraRewards = {
    update: update,
    openMap: function () { openDialog('map'); },
    openPicker: function () { openDialog('picker'); },
    checkout: function () {
      if (!config) {
        secureFallbackCheckout(parseJSON('avora-rewards-config', {}));
        return;
      }
      beginCheckout(false);
    },
    getState: function () { return state; }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
